import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./global.css";
import MonthlyBankSummary from "./MonthlyBankSummary";
import BankPopup from "./bankPopup";
import "./FinancePages.css";
import { mergeCategories, loadCustomCategories, saveCustomCategories, REPAYMENT_TYPE } from "../utils/bankSpendCategories";
import { entryAccountId, entryIsPersonal, personalTypeOf, recentCompletedMonths } from "../utils/profitCalc";
import { bankJobMatchesForDate, bankJobsOnDay, bankJobSite, bankJobScope } from "../utils/bankJobMatching";
import { isRecurring } from "../utils/recurringOccurrenceUtils";
import { bankPayrollByMonth, isBankPayrollLine, monthsRange } from "../utils/profitLaborCheck";
import { fetchDailyLaborDays, summarizeDailyLabor, usersToMap } from "../utils/techDailyPay";
import {
  normalizeContractorPayment,
  contractorCandidates,
  autoPick,
  nearbyPayments,
  looksLikePayroll,
  looksLikeContractorPay,
} from "../utils/payMatching";

const todayKey = () => new Date().toISOString().slice(0, 10);

// Customer payments are entered on Deposits and Job Estimate Spreadsheet only.
// Bank money-in lines are a record of what hit the bank; they no longer create
// a second payment on Deposits (that was a source of duplicates).
const BANK_MONEY_IN_CREATES_DEPOSITS = false;

// Money in that has to be paid back (owner's personal money or a loan).
const REIMBURSABLE_ACTION = "reimbursable_do_not_count";

// Money out that is a contractor payment you already recorded (Spreadsheet Sub
// Pay or Contractor Pay). It counts ONCE: Profit uses this bank line, and no
// second ledger entry is created because the contractor payment already made one.
const CONTRACTOR_ACTION = "matched_contractor_pay";

// Categories that must never become a monthly expense from the bank: payroll
// and contractor pay are already counted from job labor / sub pay, so a
// monthly expense would count them twice on the Job Estimate Spreadsheet.
const NO_MONTHLY_CATEGORIES = new Set(["Payroll", "Subcontractor"]);

const emptyEntry = {
  date: todayKey(),
  type: "moneyOut", // moneyIn | moneyOut
  amount: "",
  bankAccountId: "",
  bankName: "",
  accountLast4: "",
  description: "",
  referenceNumber: "",
  statementSection: "",
  statementPeriod: "",
  category: "Materials",
  categoryOther: "",
  vendorOrPayer: "",
  property: "",
  jobId: "",
  jobOccurrenceDate: "",
  wo: "",
  invoiceNumber: "",
  company: "Priority Pest Defense",
  paymentMethod: "Bank Statement",
  recurrenceType: "oneTime", // oneTime | recurring
  recurringFrequency: "Monthly",
};


const emptyBankAccount = {
  bankName: "",
  accountLast4: "",
  nickname: "",
  accountType: "operating", // operating | creditCard | personal
};

const bankAccountTypeLabel = (accountType = "operating") => {
  if (accountType === "creditCard") return "Credit Card";
  if (accountType === "personal") return "Personal";
  return "Operating";
};

const bankAccountLabel = (account = {}) => {
  const bank = String(account.bankName || "").trim();
  const last4 = String(account.accountLast4 || "").replace(/\D/g, "").slice(-4);
  const nickname = String(account.nickname || "").trim();
  const accountType = account.accountType ? bankAccountTypeLabel(account.accountType) : "";
  const main = [bank || "Unnamed Bank", last4 ? `•••• ${last4}` : ""].filter(Boolean).join(" ");
  const extra = [nickname, accountType].filter(Boolean).join(" - ");
  return extra ? `${main} - ${extra}` : main;
};

const LoadingSpinner = ({ label = "Loading" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "bank-entry-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const toNum = (value) => {
  const n = Number(String(value || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toDateKey = (value) => {
  if (!value) return "";
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object" && value.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
};

const daysBetween = (a, b) => {
  const ad = new Date(`${a}T00:00:00`);
  const bd = new Date(`${b}T00:00:00`);
  if (Number.isNaN(ad.getTime()) || Number.isNaN(bd.getTime())) return 99999;
  return Math.round(Math.abs(bd - ad) / 86400000);
};

const DEFAULT_CLOSE_MATCH_FEE = 150;

const amountDifference = (a, b) => Math.abs(toNum(a) - toNum(b));

const amountMatchType = (bankAmount, recordAmount, nearbyFees = []) => {
  const b = toNum(bankAmount);
  const r = toNum(recordAmount);
  if (!b || !r) return null;

  const diff = Math.abs(b - r);
  if (diff <= 0.009) {
    return { type: "exact", diff: 0, feeMatched: null, note: "Exact amount match" };
  }

  const feeMatched = nearbyFees.find((fee) => Math.abs(toNum(fee.amount) - diff) <= 0.009);
  if (feeMatched) {
    return {
      type: "fee_adjusted",
      diff,
      feeMatched,
      note: `Close match: amount differs by ${money(diff)}, matching nearby fee ${feeMatched.date || ""} ${feeMatched.description || ""}`.trim(),
    };
  }

  if (diff <= DEFAULT_CLOSE_MATCH_FEE) {
    return {
      type: "close",
      diff,
      feeMatched: null,
      note: `Possible close match: amount differs by ${money(diff)} (within the $150 review tolerance). Manually confirm the date, payer, property, WO/invoice, and possible deposit fee before matching.`,
    };
  }

  return null;
};

const getEntryDetails = (entry = {}) => {
  const pieces = [];
  if (entry.statementSection) pieces.push(entry.statementSection);
  if (entry.referenceNumber) pieces.push(`Ref ${entry.referenceNumber}`);
  if (entry.statementPeriod) pieces.push(`Period ${entry.statementPeriod}`);
  if (entry.matchNote) pieces.push(entry.matchNote);
  return pieces.filter(Boolean).join(" • ");
};

const getApprovedInvoiceNumber = (invoice = {}) =>
  String(invoice.wo || invoice.invoiceNumber || invoice.id || "").trim();

const getDepositInvoiceNumber = (deposit = {}) =>
  String(deposit.invoiceNumber || deposit.wo || deposit.workOrder || "").trim();

const accountingActionLabels = {
  count_as_new: "Count in books",
  count_as_income: "Count as income",
  count_as_expense: "Count as business expense",
  matched_existing_do_not_count: "Matched existing - do not count again",
  credit_card_payment_transfer: "Credit card payment / transfer - do not count",
  internal_transfer_do_not_count: "Internal transfer - do not count",
  owner_contribution_do_not_count: "Owner contribution / capital - do not count as income",
  owner_draw_personal_do_not_count: "Personal / owner draw - do not count as business expense",
  reimbursable_do_not_count: "Reimbursable (has to be paid back) - not income",
  matched_contractor_pay: "Contractor pay (matched) - counted once",
  refund_reimbursement_offset: "Refund / reimbursement - offset/review",
  bank_correction_do_not_count: "Bank correction / reversal - do not count",
};

const getDefaultAccountingAction = (entry = {}) => {
  if (entry.type === "moneyIn" && entry.reimbursable) return REIMBURSABLE_ACTION;
  if (entry.accountingAction) return entry.accountingAction;
  if (entry.matchedExistingPayment) return "matched_existing_do_not_count";
  if (entry.accountType === "creditCard" && entry.type === "moneyIn") return "credit_card_payment_transfer";
  if (entry.category === "Credit Card Payment") return "credit_card_payment_transfer";
  if (entry.category === "Internal Transfer") return "internal_transfer_do_not_count";
  if (entry.category === "Owner Transfer / Capital") return "owner_contribution_do_not_count";
  if (entry.category === "Personal / Owner Draw") return "owner_draw_personal_do_not_count";
  if (entry.category === "Personal Life Insurance") return "owner_draw_personal_do_not_count";
  if (entry.category === "Refund / Reimbursement") return "refund_reimbursement_offset";
  if (entry.category === "Bank Correction / Reversal") return "bank_correction_do_not_count";
  if (entry.type === "moneyIn") return "count_as_income";
  return "count_as_expense";
};

const getCategoryDrivenEntryChanges = (entry = {}, nextCategory = "") => {
  const category = nextCategory || entry.category || "Other";
  const base = {
    ...entry,
    category,
    categoryOther: category === "Other" ? entry.categoryOther || "" : "",
  };

  if (category === "Credit Card Payment") {
    // On the card's own statement the payment is money IN. On a bank account it's
    // money OUT (paying the card), so keep it as money out there.
    return {
      ...base,
      type: base.accountType === "creditCard" ? "moneyIn" : base.type,
      accountingAction: "credit_card_payment_transfer",
    };
  }
  if (category === "Personal / Owner Draw" || category === "Personal Life Insurance") {
    return { ...base, type: "moneyOut", accountingAction: "owner_draw_personal_do_not_count" };
  }
  if (category === "Owner Transfer / Capital") {
    return { ...base, type: "moneyIn", accountingAction: "owner_contribution_do_not_count" };
  }
  if (category === "Internal Transfer") {
    return { ...base, accountingAction: "internal_transfer_do_not_count" };
  }
  if (category === "Refund / Reimbursement") {
    return { ...base, type: "moneyIn", accountingAction: "refund_reimbursement_offset" };
  }
  if (category === "Bank Correction / Reversal") {
    return { ...base, accountingAction: "bank_correction_do_not_count" };
  }
  if (category === "Customer Payment" || category === "Bank Deposit") {
    return { ...base, type: "moneyIn", accountingAction: "count_as_income" };
  }

  return {
    ...base,
    accountingAction: base.type === "moneyIn" ? "count_as_income" : "count_as_expense",
  };
};

const accountingActionOptionsForEntry = (entry = {}) => {
  if (entry.type === "moneyIn") {
    return [
      ["count_as_income", "Count as income"],
      ["matched_existing_do_not_count", "Matched existing payment - do not count again"],
      ["credit_card_payment_transfer", "Credit card payment / transfer - do not count"],
      ["internal_transfer_do_not_count", "Internal transfer between accounts - do not count"],
      ["owner_contribution_do_not_count", "Owner contribution / capital - do not count as income"],
      [REIMBURSABLE_ACTION, "Reimbursable (has to be paid back) - not income"],
      ["refund_reimbursement_offset", "Refund / reimbursement - offset/review"],
      ["bank_correction_do_not_count", "Bank correction / reversal - do not count"],
    ];
  }

  return [
    ["count_as_expense", "Count as business expense"],
    ["credit_card_payment_transfer", "Credit card payment / transfer - do not count"],
    ["internal_transfer_do_not_count", "Internal transfer between accounts - do not count"],
    ["owner_draw_personal_do_not_count", "Personal / owner draw - do not count as business expense"],
    [CONTRACTOR_ACTION, "Contractor pay (matched) - counted once"],
    ["refund_reimbursement_offset", "Refund / reimbursement - offset/review"],
    ["bank_correction_do_not_count", "Bank correction / reversal - do not count"],
  ];
};

const accountingActionLabel = (entry = {}) => {
  const action = getDefaultAccountingAction(entry);
  return accountingActionLabels[action] || accountingActionLabels.count_as_new;
};

const accountingActionCounts = (entry = {}) => {
  const action = getDefaultAccountingAction(entry);
  return ["count_as_new", "count_as_income", "count_as_expense"].includes(action);
};

/** Money out that counts as a business expense (including matched contractor pay). */
const countsAsBusinessOut = (entry = {}) =>
  accountingActionCounts(entry) || getDefaultAccountingAction(entry) === CONTRACTOR_ACTION;

const entryIsCounted = (entry = {}) =>
  !entry.matchedExistingPayment && accountingActionCounts(entry);

const normalizeImportedDate = (rawDate, fallbackYear = new Date().getFullYear()) => {
  const text = String(rawDate || "").trim();
  const parts = text.split(/[/-]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return "";

  const month = Number(parts[0]);
  const day = Number(parts[1]);
  let year = parts[2] ? Number(parts[2]) : Number(fallbackYear);
  if (year < 100) year += 2000;

  if (!month || !day || !year || month > 12 || day > 31) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const businessCategories = [
  "Accounting / Bookkeeping",
  "Advertising / Marketing",
  "Auto Insurance",
  "Bank Deposit",
  "Bank Correction / Reversal",
  "Bank Fee",
  "Credit Card Payment",
  "Customer Payment",
  "Internal Transfer",
  "Electric",
  "Employee Reimbursement",
  "Equipment Purchase",
  "Equipment Rental",
  "Fuel / Gas",
  "Gas Utility",
  "General Liability Insurance",
  "Health / Benefits",
  "Personal Life Insurance",
  "Internet",
  "Legal / Professional Fees",
  "Licenses / Permits",
  "Loan Payment",
  "Lodging",
  "Materials",
  "Meals / Food",
  "Merchant Fees",
  "Office Supplies",
  "Other",
  "Owner Transfer / Capital",
  "Personal / Owner Draw",
  "Parking",
  "Payroll",
  "Payroll Taxes",
  "Phone",
  "Postage / Shipping",
  "Printing",
  "Refund / Reimbursement",
  "Rent / Office Lease",
  "Repairs / Maintenance",
  "Safety / PPE",
  "Shop Supplies",
  "Software / Subscriptions",
  "Storage",
  "Subcontractor",
  "Taxes",
  "Tolls / EZ Pass",
  "Tools",
  "Training / Education",
  "Travel",
  "Uniforms",
  "Utilities",
  "Vehicle Maintenance",
  "Vehicle Payment",
  "Waste Disposal",
  "Water / Sewer",
  "Workers Comp",
];

const renderCategoryOptions = () =>
  businessCategories.map((category) => (
    <option key={category} value={category}>
      {category}
    </option>
  ));

const effectiveCategory = (entry = {}) => {
  const category = entry.category || "Other";
  const other = String(entry.categoryOther || "").trim();
  return category === "Other" && other ? other : category;
};


const normalizeDuplicateText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

const getBankEntryDuplicateKey = (entry = {}) => {
  const date = toDateKey(entry.date);
  const type = entry.type || entry.transactionType || "";
  const amount = toNum(entry.amount).toFixed(2);
  const ref = normalizeDuplicateText(entry.referenceNumber || "");
  const bank = normalizeDuplicateText(entry.bankName || "");
  const last4 = normalizeDuplicateText(entry.accountLast4 || "");
  const section = normalizeDuplicateText(entry.statementSection || "");
  const desc = normalizeDuplicateText(entry.description || entry.transactionDetail || "");
  const period = normalizeDuplicateText(entry.statementPeriod || "");
  return [date, type, amount, bank, last4, ref, section, desc, period].join("|");
};

const bankEntriesLookDuplicate = (a = {}, b = {}) => {
  if (!a || !b) return false;
  const aKey = a.duplicateKey || getBankEntryDuplicateKey(a);
  const bKey = b.duplicateKey || getBankEntryDuplicateKey(b);
  if (aKey && bKey && aKey === bKey) return true;

  const sameDate = toDateKey(a.date) === toDateKey(b.date);
  const sameType = String(a.type || a.transactionType || "") === String(b.type || b.transactionType || "");
  const sameAmount = Math.abs(toNum(a.amount) - toNum(b.amount)) <= 0.009;
  const aRef = normalizeDuplicateText(a.referenceNumber || "");
  const bRef = normalizeDuplicateText(b.referenceNumber || "");
  const refsMatch = aRef && bRef && aRef === bRef;
  const descMatch = normalizeDuplicateText(a.description || a.transactionDetail || "") === normalizeDuplicateText(b.description || b.transactionDetail || "");
  return sameDate && sameType && sameAmount && (refsMatch || descMatch);
};

const monthlyExpenseLooksDuplicate = (row = {}, expense = {}) => {
  const sameAmount = Math.abs(toNum(row.amount) - toNum(expense.amount)) <= 0.009;
  if (!sameAmount) return false;

  const rowCategory = normalizeDuplicateText(effectiveCategory(row));
  const expenseType = normalizeDuplicateText(expense.billType || expense.category || expense.type || "");
  const rowVendor = normalizeDuplicateText(row.vendorOrPayer || row.description || "");
  const expenseVendor = normalizeDuplicateText(expense.vendorName || expense.vendor || expense.description || "");

  const categoryMatch = rowCategory && expenseType && (rowCategory === expenseType || rowCategory.includes(expenseType) || expenseType.includes(rowCategory));
  const vendorMatch = rowVendor && expenseVendor && (rowVendor.includes(expenseVendor) || expenseVendor.includes(rowVendor));

  return categoryMatch || vendorMatch;
};

const guessCategory = (description = "", type = "moneyOut", amountValue = 0) => {
  const d = description.toLowerCase();
  const cleanAmount = toNum(amountValue);

  if (/\blife\b|life insurance|colonial life|globe life|new york life|northwestern mutual|prudential|metlife|transamerica|massmutual|mutual of omaha/.test(d)) return "Personal Life Insurance";

  if (type === "moneyIn") {
    if (/refund|reimburse|reimbursement|reverse|credit/.test(d)) return "Refund / Reimbursement";
    if (/transfer from|owner|capital|xxxxx/.test(d)) return "Owner Transfer / Capital";
    if (/deposit|payment|check deposit|mobile deposit|ach addition|customer/.test(d)) return "Customer Payment";
    return "Bank Deposit";
  }

  if (/ez\s?pass|e-zpass|toll/.test(d)) return "Tolls / EZ Pass";
  if (/ferguson|hardware|home depot|lowes|sherwin|supply|lumber|material|concrete|abc supply|84 lumber|floor|paint|plumb|electrical supply/.test(d)) return "Materials";
  if (/giant|weis|wegmans|safeway|food lion|harris teeter|kroger|aldi|lidl|trader joe|whole foods|costco|bj'?s|sam'?s club|grocery|market|supermarket|restaurant|mcdonald|chick|food|pizza|subway|dunkin|starbucks|cafe|doordash|ubereats/.test(d)) return "Meals / Food";
  if (/7[-\s]?eleven|7eleven|wawa|exxon|shell|bp|sheetz|sunoco|royal farms|chevron|citgo|marathon|valero|mobil|speedway|circle k|liberty gas|gas station/.test(d)) {
    return cleanAmount > 0 && cleanAmount < 50 ? "Meals / Food" : "Fuel / Gas";
  }
  if (/gas|fuel/.test(d)) return "Fuel / Gas";
  if (/payroll|adp|gusto|paychex|salary|wage|direct dep/.test(d)) return "Payroll";
  if (/941|payroll tax|withholding|irs|state tax/.test(d)) return "Payroll Taxes";
  if (/subcontract|sub contractor|1099|contractor/.test(d)) return "Subcontractor";
  if (/workers comp|work comp/.test(d)) return "Workers Comp";
  if (/general liability|gl ins|liability/.test(d)) return "General Liability Insurance";
  if (/auto ins|vehicle ins|erie|state farm|progressive|geico|usaa/.test(d)) return "Auto Insurance";
  if (/insurance|benefit|health|dental|vision/.test(d)) return "Health / Benefits";
  if (/rent|lease|office lease/.test(d)) return "Rent / Office Lease";
  if (/electric|dominion|bge|power/.test(d)) return "Electric";
  if (/water|sewer/.test(d)) return "Water / Sewer";
  if (/gas utility|washington gas|natural gas/.test(d)) return "Gas Utility";
  if (/comcast|xfinity|internet|verizon|fios/.test(d)) return "Internet";
  if (/phone|wireless|at&t|att|tmobile|t-mobile/.test(d)) return "Phone";
  if (/quickbooks|qbooks|intuit|software|subscription|microsoft|google|adobe|dropbox|icloud/.test(d)) return "Software / Subscriptions";
  if (/accounting|bookkeep|cpa/.test(d)) return "Accounting / Bookkeeping";
  if (/legal|attorney|lawyer|professional/.test(d)) return "Legal / Professional Fees";
  if (/license|permit|registration/.test(d)) return "Licenses / Permits";
  if (/carmax|auto loan|vehicle payment|truck payment|finance/.test(d)) return "Vehicle Payment";
  if (/vehicle|auto|truck|repair|tires|oil change|mechanic|maintenance/.test(d)) return "Vehicle Maintenance";
  if (/parking|garage/.test(d)) return "Parking";
  if (/office depot|staples|office supply/.test(d)) return "Office Supplies";
  if (/postage|shipping|ups|fedex|usps/.test(d)) return "Postage / Shipping";
  if (/adwords|facebook|marketing|advertising|website|domain/.test(d)) return "Advertising / Marketing";
  if (/print|copies|sign/.test(d)) return "Printing";
  if (/bank fee|service fee|overdraft|wire fee|monthly fee|returned item|nsf/.test(d)) return "Bank Fee";
  if (/owner draw|personal|draw|distribution/.test(d)) return "Personal / Owner Draw";
  if (/cardmember|credit card|ccpymt|crcardpmt|capital one|wells fargo card|chase credit/.test(d)) return "Credit Card Payment";
  if (/loan|pennymac|mtge|mortgage|amerihome/.test(d)) return "Loan Payment";
  if (/merchant|stripe|square|paypal fee/.test(d)) return "Merchant Fees";
  if (/training|course|education|seminar/.test(d)) return "Training / Education";
  if (/safety|ppe|gloves|mask|hard hat|vest/.test(d)) return "Safety / PPE";
  if (/uniform|shirt|boots/.test(d)) return "Uniforms";
  if (/dump|landfill|waste|trash|disposal/.test(d)) return "Waste Disposal";
  if (/storage|unit/.test(d)) return "Storage";
  if (/hotel|lodging|airbnb/.test(d)) return "Lodging";
  if (/travel|flight|airline|rental car/.test(d)) return "Travel";
  if (/tool|harbor freight|equipment/.test(d)) return "Tools";

  return "Other";
};

const guessType = (line = "", amountText = "") => {
  const text = line.toLowerCase();
  if (/deposit|credit|payment received|mobile deposit|remote deposit|zelle received|ach credit/.test(text)) {
    return "moneyIn";
  }
  if (/debit|withdrawal|purchase|check paid|ach debit|fee|payment to|card|pos|atm|e-zpass|ez pass/.test(text)) {
    return "moneyOut";
  }
  if (/\(|-/.test(amountText)) return "moneyOut";
  return "moneyOut";
};

const extractAmountCandidates = (line = "") => {
  const matches = line.match(/\(?-?\$?\d{1,3}(?:,\d{3})*(?:\.\d{2})\)?|\(?-?\$?\d+\.\d{2}\)?/g) || [];
  return matches
    .map((raw) => {
      const isNegative = raw.includes("(") || raw.includes("-");
      const clean = toNum(raw);
      return { raw, amount: Math.abs(clean), signedAmount: isNegative ? -Math.abs(clean) : clean };
    })
    .filter((item) => item.amount > 0);
};

const makeImportRow = ({ index, date, type, amount, description, section, statementName, rawLine, referenceNumber = "", statementPeriod = "", transactionDetail = "", bankAccountId = "", bankName = "", accountLast4 = "" }) => {
  const guessedCategory = guessCategory(`${section || ""} ${description}`, type, amount);
  const baseRow = {
    tempId: `${Date.now()}_${index}_${Math.random().toString(36).slice(2)}`,
    selected: true,
    recurrenceType: "oneTime",
    recurringFrequency: "Monthly",
    duplicateWarning: "",
    monthlyDuplicateWarning: "",
    status: "Needs Review",
    date,
    type,
    amount,
    bankAccountId,
    bankName: bankName || statementName || "PDF Bank Statement",
    accountLast4,
    description: description || "",
    referenceNumber,
    statementPeriod,
    transactionDetail,
    statementName: statementName || "",
    statementFileName: statementName || "",
    vendorOrPayer: description.slice(0, 80),
    category: guessedCategory,
    categoryOther: "",
    property: "",
    jobId: "",
    wo: "",
    invoiceNumber: "",
    company: "Priority Pest Defense",
    matchedExistingPayment: false,
    matchedCollection: "",
    matchedSourceId: "",
    matchedInvoiceNumber: "",
    accountingAction: type === "moneyIn" ? "count_as_income" : "count_as_expense",
    importSource: "pdf_upload",
    statementSection: section || "",
    rawLine,
  };
  const finalRow = getCategoryDrivenEntryChanges(baseRow, guessedCategory);
  return {
    ...finalRow,
    duplicateKey: getBankEntryDuplicateKey({ ...finalRow, referenceNumber, statementSection: section || "", statementPeriod }),
  };
};

const parsePncStatementText = (text, statementName = "") => {
  const lines = String(text || "")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const joinedText = lines.join(" ");
  const periodMatch = joinedText.match(/For the Period\s+(\d{1,2}\/\d{1,2}\/20\d{2})\s+to\s+(\d{1,2}\/\d{1,2}\/20\d{2})/i);
  const statementPeriod = periodMatch ? `${periodMatch[1]} to ${periodMatch[2]}` : "";
  const fullYearMatch = periodMatch || joinedText.match(/For the Period\s+\d{1,2}\/\d{1,2}\/(20\d{2})/i) || joinedText.match(/\b(20\d{2})\b/);
  const fallbackYear = fullYearMatch ? Number(fullYearMatch[2] ? fullYearMatch[2].slice(-4) : fullYearMatch[1]) : new Date().getFullYear();

  const moneyInSections = new Set(["Deposits", "ACH Additions", "Other Additions"]);
  const moneyOutSections = new Set([
    "ATM/Misc. Debit Card Transactions",
    "ACH Deductions",
    "Service Charges and Fees",
    "Other Deductions",
    "Detail of Services Used During Current Period",
  ]);
  const allSections = new Set([...moneyInSections, ...moneyOutSections]);

  const isSectionHeading = (line) => {
    const normalized = line.replace(/\s+-\s+continued/i, "").trim();
    if (/^Detail of Services Used During Current Period/i.test(normalized)) return "Detail of Services Used During Current Period";
    if (allSections.has(normalized)) return normalized;
    if (/^ACH Deductions\b/i.test(normalized)) return "ACH Deductions";
    return "";
  };

  const stopSection = (line) =>
    /^(Daily Balance|Detail of Services|Balance Summary|Deposits and Other Additions$|Checks and Other Deductions$|Activity Detail|Business Checking|Page \d+|Date posted|Amount|Transaction description|Reference number)/i.test(line);

  const rows = [];
  let section = "";
  let pending = null;
  let activityStarted = false;
  let serviceChargeDate = "";

  const servicePostDateMatch = joinedText.match(/posted to your account on\s+(\d{1,2}\/\d{1,2}\/20\d{2})/i);
  if (servicePostDateMatch) {
    serviceChargeDate = normalizeImportedDate(servicePostDateMatch[1], fallbackYear);
  }

  const flushPending = () => {
    if (!pending) return;
    const type = moneyInSections.has(pending.section) ? "moneyIn" : "moneyOut";
    rows.push(
      makeImportRow({
        index: rows.length,
        date: pending.date,
        type,
        amount: pending.amount,
        description: pending.description.trim(),
        section: pending.section,
        statementName,
        rawLine: pending.rawLine,
        referenceNumber: pending.referenceNumber || "",
        statementPeriod,
        transactionDetail: pending.transactionDetail || pending.description || "",
      })
    );
    pending = null;
  };

  lines.forEach((line) => {
    if (/^Activity Detail$/i.test(line)) {
      flushPending();
      activityStarted = true;
      section = "";
      return;
    }

    // Do not read the first-page summary/daily-balance tables as transactions.
    // PNC transaction rows start after the Activity Detail heading.
    if (!activityStarted) return;

    const heading = isSectionHeading(line);
    if (heading) {
      flushPending();
      section = heading;
      return;
    }

    if (!section || !allSections.has(section)) return;
    if (stopSection(line)) return;
    if (/continued on next page/i.test(line)) return;

    if (section === "Detail of Services Used During Current Period") {
      const feeMatch = line.match(/^(Monthly Account Maintenance Fee)\s+\d+\s+([\d,]+\.\d{2})$/i);
      if (feeMatch) {
        flushPending();
        const date = serviceChargeDate || (statementPeriod ? normalizeImportedDate(statementPeriod.split(" to ")[1], fallbackYear) : "");
        const amount = toNum(feeMatch[2]);
        if (date && amount > 0) {
          rows.push(
            makeImportRow({
              index: rows.length,
              date,
              type: "moneyOut",
              amount,
              description: feeMatch[1],
              section,
              statementName,
              rawLine: line,
              referenceNumber: "",
              statementPeriod,
              transactionDetail: `${feeMatch[1]} - posted per statement service detail`,
            })
          );
        }
      }
      return;
    }

    const rowMatch = line.match(/^(\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?)\s+([\d,]+\.\d{2})\s*(.*)$/);
    if (rowMatch) {
      flushPending();
      const date = normalizeImportedDate(rowMatch[1], fallbackYear);
      const amount = toNum(rowMatch[2]);
      let description = (rowMatch[3] || "").trim();
      let referenceNumber = "";

      // PNC reference numbers are often the last long numeric token. Save them separately.
      const refMatch = description.match(/\s+(\d{7,})\s*$/);
      if (refMatch) {
        referenceNumber = refMatch[1];
        description = description.replace(/\s+\d{7,}\s*$/, "").trim();
      }

      if (date && amount > 0) {
        pending = {
          section,
          date,
          amount,
          description,
          referenceNumber,
          transactionDetail: description,
          rawLine: line,
        };
      }
      return;
    }

    // Continuation line for a PNC row, usually extra merchant/reference detail.
    if (pending && !/^\d{1,2}[/-]\d{1,2}/.test(line)) {
      const refMatch = line.match(/\s+(\d{7,})\s*$/);
      if (refMatch && !pending.referenceNumber) pending.referenceNumber = refMatch[1];
      const cleanContinuation = line.replace(/\s+\d{7,}\s*$/, "").trim();
      if (cleanContinuation && !/^(Effective\s+\d{1,2}-\d{1,2}-\d{2,4})$/i.test(cleanContinuation)) {
        pending.description = `${pending.description} ${cleanContinuation}`.trim();
        pending.transactionDetail = `${pending.transactionDetail || ""} ${cleanContinuation}`.trim();
        pending.rawLine = `${pending.rawLine} ${line}`.trim();
      }
    }
  });

  flushPending();
  return rows;
};

const parseStatementText = (text, statementName = "") => {
  const pncRows = parsePncStatementText(text, statementName);
  if (pncRows.length) return pncRows;

  const lines = String(text || "")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const fullYearMatch = lines.join(" ").match(/\b(20\d{2})\b/);
  const fallbackYear = fullYearMatch ? Number(fullYearMatch[1]) : new Date().getFullYear();

  const rows = [];
  lines.forEach((line, index) => {
    const dateMatch = line.match(/\b(\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?)\b/);
    if (!dateMatch) return;

    const amounts = extractAmountCandidates(line);
    if (!amounts.length) return;

    // If the line contains a running balance, it is often the last amount. Use the next-to-last as the transaction amount.
    const amountItem = amounts.length >= 2 ? amounts[amounts.length - 2] : amounts[amounts.length - 1];
    const amount = amountItem.amount;
    if (!amount || amount <= 0) return;

    const date = normalizeImportedDate(dateMatch[1], fallbackYear);
    if (!date) return;

    let description = line
      .replace(dateMatch[1], "")
      .replace(amountItem.raw, "")
      .replace(/\s+/g, " ")
      .trim();
    let referenceNumber = "";
    const refMatch = description.match(/\s+(\d{7,})\s*$/);
    if (refMatch) {
      referenceNumber = refMatch[1];
      description = description.replace(/\s+\d{7,}\s*$/, "").trim();
    }

    const type = guessType(line, amountItem.raw);

    rows.push(
      makeImportRow({
        index,
        date,
        type,
        amount,
        description,
        section: "Generic PDF Import",
        statementName,
        rawLine: line,
        referenceNumber,
        transactionDetail: description,
      })
    );
  });

  return rows;
};


const monthKeyFromEntry = (entry = {}) => {
  const date = toDateKey(entry.date);
  return date ? date.slice(0, 7) : "No Date";
};

const monthLabelFromKey = (monthKey = "") => {
  if (!monthKey || monthKey === "No Date") return "No Date";
  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return monthKey;
  return date.toLocaleString("en-US", { month: "long", year: "numeric" });
};

const getStatementName = (entry = {}) =>
  String(entry.statementName || entry.statementFileName || entry.pdfFileName || entry.statementPeriod || entry.statementSection || "Manual / Single Entries").trim();

const getStatementGroupKey = (entry = {}) => {
  const bankKey = [
    String(entry.bankAccountId || "").trim(),
    normalizeDuplicateText(entry.bankName || entry.bankAccountLabel || ""),
    String(entry.accountLast4 || "").replace(/\D/g, "").slice(-4),
  ].filter(Boolean).join("|") || "unknown-bank";
  const statementKey = normalizeDuplicateText(getStatementName(entry)) || "manual-single-entries";
  const periodKey = normalizeDuplicateText(entry.statementPeriod || "") || monthKeyFromEntry(entry);
  return `${bankKey}|${statementKey}|${periodKey}`;
};

const getStatementGroupLabel = (entries = []) => {
  const first = entries[0] || {};
  const statementName = getStatementName(first);
  const period = first.statementPeriod ? ` • ${first.statementPeriod}` : "";
  return `${bankAccountLabel(first)} • ${statementName}${period}`;
};

// PDF.js comes from react-pdf (already used by Reports). react-pdf v7 ships
// pdfjs-dist 3.x (pdf.js / pdf.worker.min.js); v8–v9 ship 4.x (pdf.mjs /
// pdf.worker.min.mjs). Importing react-pdf's own `pdfjs` export always
// resolves — no hard-coded pdfjs-dist file path that could be missing in one
// version and break the build — and is the exact same PDF.js copy Reports
// uses, so the worker version always matches.
const loadPdfJs = async () => {
  let pdfjsLib = null;
  try {
    const mod = await import("react-pdf");
    pdfjsLib = mod?.pdfjs || mod?.default?.pdfjs || null;
  } catch (err) {
    console.error("react-pdf could not be loaded:", err);
  }
  if (!pdfjsLib || typeof pdfjsLib.getDocument !== "function") {
    throw new Error("The PDF reader (react-pdf / pdfjs-dist) could not be loaded. Run npm install, then restart the app.");
  }

  if (pdfjsLib.GlobalWorkerOptions) {
    const version = String(pdfjsLib.version || "");
    const major = parseInt(version, 10) || 4;
    const workerFile = major >= 4 ? "pdf.worker.min.mjs" : "pdf.worker.min.js";
    const wanted =
      process.env.REACT_APP_PDF_WORKER_URL ||
      (version ? `https://unpkg.com/pdfjs-dist@${version}/build/${workerFile}` : "");
    // Reports sets the same URL on this same object; only fill it when empty.
    if (wanted && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = wanted;
    }
  }
  return pdfjsLib;
};

async function readPdfText(file) {
  const pdfjsLib = await loadPdfJs();

  const buffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(buffer),
    useWorkerFetch: false,
    isEvalSupported: false,
  });

  const pdf = await loadingTask.promise;

  const pages = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent({ normalizeWhitespace: true, disableCombineTextItems: false });
    const rowsByY = new Map();

    content.items.forEach((item) => {
      const y = Math.round(item.transform?.[5] || 0);
      if (!rowsByY.has(y)) rowsByY.set(y, []);
      rowsByY.get(y).push({ x: item.transform?.[4] || 0, text: item.str || "" });
    });

    const pageLines = [...rowsByY.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([, items]) =>
        items
          .sort((a, b) => a.x - b.x)
          .map((i) => i.text)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim()
      )
      .filter(Boolean);

    pages.push(pageLines.join("\n"));
  }

  return pages.join("\n");
}

export default function BankStatementEntries() {
  // "entries" = the original statement entry screen, "monthly" = Monthly Bank Summary tab
  const [pageTab, setPageTab] = useState("entries");
  const [entries, setEntries] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountForm, setBankAccountForm] = useState(emptyBankAccount);
  const [selectedUploadBankAccountId, setSelectedUploadBankAccountId] = useState("");
  const [savingBankAccount, setSavingBankAccount] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState("");
  const [editEntryForm, setEditEntryForm] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [form, setForm] = useState(emptyEntry);
  const [selectedMoneyInMatch, setSelectedMoneyInMatch] = useState("");
  const [selectedJobMatch, setSelectedJobMatch] = useState("");
  const [matchWindowDays, setMatchWindowDays] = useState(5);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pdfFileName, setPdfFileName] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importingPdf, setImportingPdf] = useState(false);
  const [importingRows, setImportingRows] = useState(false);
  const [rawExtractedText, setRawExtractedText] = useState("");
  const [showRawText, setShowRawText] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showRecentEntries, setShowRecentEntries] = useState(false);
  const [openEntryMonths, setOpenEntryMonths] = useState({});
  const [openEntryStatements, setOpenEntryStatements] = useState({});
  // Banks & Cards status + personal types
  const [manualSummaries, setManualSummaries] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [showAddBank, setShowAddBank] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("all"); // all | moneyOut | personal
  const [contractorPays, setContractorPays] = useState([]);
  const [openContractorPickers, setOpenContractorPickers] = useState({});
  // Tech Daily Pay (dailyLabor) — shown next to bank payroll to reconcile only.
  const [laborDays, setLaborDays] = useState([]);
  const [laborUsers, setLaborUsers] = useState([]);

  const loadLabor = async () => {
    try {
      const [days, usersSnap] = await Promise.all([
        fetchDailyLaborDays(),
        getDocs(collection(db, "users")),
      ]);
      setLaborDays(days);
      setLaborUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn("Could not load Tech Daily Pay for the payroll check:", err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    loadLabor();
    try {
      const [entriesSnap, bankAccountsSnap, jobsSnap, invoicesSnap, depositsSnap, monthlyExpensesSnap, summariesSnap, custom, subPaySnap, legacySubPaySnap] = await Promise.all([
        getDocs(collection(db, "bankStatementEntries")),
        getDocs(collection(db, "bankStatementAccounts")),
        getDocs(collection(db, "jobs")),
        getDocs(collection(db, "approvedInvoices")),
        getDocs(collection(db, "deposits")),
        getDocs(collection(db, "monthly_expenses")),
        getDocs(collection(db, "bankMonthlySummaries")),
        loadCustomCategories(),
        getDocs(collection(db, "subcontractorPay")),
        getDocs(collection(db, "subcontractorPayments")).catch(() => ({ docs: [] })),
      ]);
      setContractorPays([
        ...subPaySnap.docs.map((d) => normalizeContractorPayment(d.id, d.data(), "subcontractorPay")),
        ...legacySubPaySnap.docs.map((d) => normalizeContractorPayment(d.id, d.data(), "subcontractorPayments")),
      ]);
      setManualSummaries(summariesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCustomCats(custom);

      setEntries(
        entriesSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
      );
      setBankAccounts(
        bankAccountsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => bankAccountLabel(a).localeCompare(bankAccountLabel(b)))
      );
      setJobs(jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setApprovedInvoices(invoicesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setDeposits(depositsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMonthlyExpenses(monthlyExpensesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error loading bank statement data:", err);
      alert("Could not load the bank statement data. Check your connection and refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const amount = toNum(form.amount);
  const entryDate = toDateKey(form.date);

  const sortedJobs = useMemo(
    () =>
      [...jobs].sort((a, b) =>
        String(a.siteName || a.propertyName || "").localeCompare(String(b.siteName || b.propertyName || ""))
      ),
    [jobs]
  );



  const groupedRecentEntries = useMemo(() => {
    const monthMap = new Map();

    entries.forEach((entry) => {
      const monthKey = monthKeyFromEntry(entry);
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          key: monthKey,
          label: monthLabelFromKey(monthKey),
          entries: [],
          statementMap: new Map(),
        });
      }

      const monthGroup = monthMap.get(monthKey);
      monthGroup.entries.push(entry);

      const statementKey = getStatementGroupKey(entry);
      if (!monthGroup.statementMap.has(statementKey)) {
        monthGroup.statementMap.set(statementKey, {
          key: `${monthKey}|${statementKey}`,
          entries: [],
        });
      }
      monthGroup.statementMap.get(statementKey).entries.push(entry);
    });

    return [...monthMap.values()]
      .sort((a, b) => String(b.key).localeCompare(String(a.key)))
      .map((monthGroup) => {
        const statements = [...monthGroup.statementMap.values()]
          .map((statementGroup) => {
            const moneyInTotal = statementGroup.entries
              .filter((entry) => entry.type === "moneyIn" && accountingActionCounts(entry))
              .reduce((sum, entry) => sum + toNum(entry.amount), 0);
            const moneyOutTotal = statementGroup.entries
              .filter((entry) => entry.type !== "moneyIn" && countsAsBusinessOut(entry))
              .reduce((sum, entry) => sum + toNum(entry.amount), 0);
            const recordOnlyTotal = statementGroup.entries
              .filter((entry) => !countsAsBusinessOut(entry))
              .reduce((sum, entry) => sum + toNum(entry.amount), 0);
            const personalTotal = statementGroup.entries
              .filter((entry) => entry.type !== "moneyIn" && entryIsPersonal(entry) && personalTypeOf(entry) !== REPAYMENT_TYPE)
              .reduce((sum, entry) => sum + toNum(entry.amount), 0);

            return {
              ...statementGroup,
              label: getStatementGroupLabel(statementGroup.entries),
              moneyInTotal,
              moneyOutTotal,
              recordOnlyTotal,
              personalTotal,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        const moneyInTotal = monthGroup.entries
          .filter((entry) => entry.type === "moneyIn" && accountingActionCounts(entry))
          .reduce((sum, entry) => sum + toNum(entry.amount), 0);
        const moneyOutTotal = monthGroup.entries
          .filter((entry) => entry.type !== "moneyIn" && countsAsBusinessOut(entry))
          .reduce((sum, entry) => sum + toNum(entry.amount), 0);
        const recordOnlyTotal = monthGroup.entries
          .filter((entry) => !countsAsBusinessOut(entry))
          .reduce((sum, entry) => sum + toNum(entry.amount), 0);
        const personalTotal = monthGroup.entries
          .filter((entry) => entry.type !== "moneyIn" && entryIsPersonal(entry) && personalTypeOf(entry) !== REPAYMENT_TYPE)
          .reduce((sum, entry) => sum + toNum(entry.amount), 0);

        return {
          ...monthGroup,
          statements,
          moneyInTotal,
          moneyOutTotal,
          recordOnlyTotal,
          personalTotal,
        };
      });
  }, [entries]);

  const toggleEntryMonth = (monthKey) => {
    setOpenEntryMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  };

  const toggleEntryStatement = (statementKey) => {
    setOpenEntryStatements((prev) => ({ ...prev, [statementKey]: !prev[statementKey] }));
  };

  const selectedUploadBankAccount = useMemo(
    () => bankAccounts.find((account) => account.id === selectedUploadBankAccountId) || null,
    [bankAccounts, selectedUploadBankAccountId]
  );

  const applyBankAccountToEntry = (entry = {}, account = null) => ({
    ...entry,
    bankAccountId: account?.id || "",
    bankName: account?.bankName || entry.bankName || "",
    accountLast4: account?.accountLast4 || entry.accountLast4 || "",
    accountType: account?.accountType || entry.accountType || "operating",
    accountingAction: account?.accountType === "creditCard" && entry.type === "moneyIn"
      ? "credit_card_payment_transfer"
      : getDefaultAccountingAction({ ...entry, accountingAction: "", accountType: account?.accountType || entry.accountType || "operating" }),
  });

  const applyBankAccountToImportRows = (accountId) => {
    setSelectedUploadBankAccountId(accountId);
    const account = bankAccounts.find((item) => item.id === accountId) || null;
    setImportRows((prev) =>
      prev.map((row) => {
        const nextRow = applyBankAccountToEntry(row, account);
        return {
          ...nextRow,
          duplicateKey: getBankEntryDuplicateKey(nextRow),
          duplicateWarning: findExistingDuplicateEntry(nextRow) ? "Already imported - duplicate" : "",
        };
      })
    );
  };

  const handleManualBankAccountChange = (accountId) => {
    const account = bankAccounts.find((item) => item.id === accountId) || null;
    setForm((prev) => applyBankAccountToEntry(prev, account));
  };

  const handleEditBankAccountChange = (accountId) => {
    const account = bankAccounts.find((item) => item.id === accountId) || null;
    setEditEntryForm((prev) => applyBankAccountToEntry(prev || {}, account));
  };

  const findNearbyBankFeesForRow = (row) => {
    const rowDate = toDateKey(row.date);
    if (!rowDate) return [];

    const sourceRows = importRows.length ? importRows : entries;
    return sourceRows
      .filter((item) => {
        if (!item || item === row) return false;
        const itemDate = toDateKey(item.date);
        const itemAmount = toNum(item.amount);
        if (!itemDate || !itemAmount) return false;
        if (daysBetween(rowDate, itemDate) > Number(matchWindowDays || 5)) return false;
        const text = `${item.description || ""} ${item.category || ""} ${item.statementSection || ""}`.toLowerCase();
        return item.type === "moneyOut" && /(express funds fee|bank fee|service fee|merchant fee|processing fee|returned item|overdraft|nsf)/i.test(text);
      })
      .map((item) => ({
        id: item.id || item.tempId || `${item.date}_${item.amount}_${item.description}`,
        date: toDateKey(item.date),
        amount: toNum(item.amount),
        description: item.description || effectiveCategory(item) || "Nearby fee",
      }));
  };

  const findMoneyInMatchesForRow = (row) => {
    const rowAmount = toNum(row.amount);
    const rowDate = toDateKey(row.date);
    if (row.type !== "moneyIn" || !rowAmount || !rowDate) return [];

    const nearbyFees = findNearbyBankFeesForRow(row);

    const buildMatchLabel = ({ date, bankAmount, recordAmount, invoiceNumber, site, description, matchInfo }) => {
      const base = `${date || "No date"} • Bank ${money(bankAmount)} ↔ Record ${money(recordAmount)} • Invoice/WO ${invoiceNumber || "N/A"} • ${site || "Unknown site"}`;
      if (!matchInfo || matchInfo.type === "exact") return base;
      return `${base} • ${matchInfo.note}`;
    };

    const byDeposit = deposits
      .map((deposit) => {
        const dDate = toDateKey(deposit.date || deposit.paidAt || deposit.checkDate || deposit.createdAt);
        const dAmount = toNum(deposit.amount);
        const diffDays = daysBetween(rowDate, dDate);
        if (!dDate || diffDays > Number(matchWindowDays || 5)) return null;

        const matchInfo = amountMatchType(rowAmount, dAmount, nearbyFees);
        if (!matchInfo) return null;

        const invoiceNumber = getDepositInvoiceNumber(deposit);
        const invoice = approvedInvoices.find((inv) => getApprovedInvoiceNumber(inv) === invoiceNumber) || {};
        const site = deposit.property || invoice.siteName || invoice.propertyName || "";

        return {
          id: `deposit:${deposit.id}`,
          collection: "deposits",
          sourceId: deposit.id,
          label: buildMatchLabel({
            date: dDate,
            bankAmount: rowAmount,
            recordAmount: dAmount,
            invoiceNumber,
            site,
            description: deposit.description || invoice.scope || "Existing deposit/payment",
            matchInfo,
          }),
          invoiceNumber,
          wo: invoiceNumber,
          property: site,
          description: deposit.description || invoice.scope || "Existing deposit/payment",
          amount: dAmount,
          bankAmount: rowAmount,
          date: dDate,
          matchType: matchInfo.type,
          matchDifference: matchInfo.diff,
          matchNote: matchInfo.note,
          matchedFeeDescription: matchInfo.feeMatched?.description || "",
          matchedFeeAmount: matchInfo.feeMatched?.amount || 0,
        };
      })
      .filter(Boolean);

    const byInvoicePaid = approvedInvoices
      .map((invoice) => {
        const invoiceAmount = toNum(invoice.amount || invoice.price || invoice.rate || invoice.invoiceAmount || invoice.total);
        const paidAmount = toNum(invoice.paidAmount);
        const invoiceKey = getApprovedInvoiceNumber(invoice);
        const depositsForInvoice = deposits.filter((d) => getDepositInvoiceNumber(d) === invoiceKey);
        const depositTotal = depositsForInvoice.reduce((sum, d) => sum + toNum(d.amount), 0);
        const effectivePaid = paidAmount || depositTotal || invoiceAmount;
        const paidDate = toDateKey(invoice.paidDate || invoice.paidAt || invoice.updatedAt || invoice.invoiceDate || invoice.dateOfService || invoice.serviceDate);
        const diffDays = daysBetween(rowDate, paidDate);

        if (!paidDate || diffDays > Number(matchWindowDays || 5)) return null;
        if (byDeposit.some((match) => match.invoiceNumber === invoiceKey && match.matchType === "exact")) return null;

        const matchInfo = amountMatchType(rowAmount, effectivePaid, nearbyFees) || amountMatchType(rowAmount, invoiceAmount, nearbyFees);
        if (!matchInfo) return null;

        const site = invoice.siteName || invoice.propertyName || "";
        return {
          id: `invoice:${invoice.id}`,
          collection: "approvedInvoices",
          sourceId: invoice.id,
          label: buildMatchLabel({
            date: paidDate,
            bankAmount: rowAmount,
            recordAmount: effectivePaid || invoiceAmount,
            invoiceNumber: invoiceKey,
            site,
            description: invoice.scope || invoice.description || invoice.serviceRows?.[0]?.serviceDescription || "Existing paid invoice",
            matchInfo,
          }),
          invoiceNumber: invoiceKey,
          wo: invoiceKey,
          property: site,
          description: invoice.scope || invoice.description || invoice.serviceRows?.[0]?.serviceDescription || "Existing paid invoice",
          amount: effectivePaid || invoiceAmount,
          bankAmount: rowAmount,
          date: paidDate,
          matchType: matchInfo.type,
          matchDifference: matchInfo.diff,
          matchNote: matchInfo.note,
          matchedFeeDescription: matchInfo.feeMatched?.description || "",
          matchedFeeAmount: matchInfo.feeMatched?.amount || 0,
        };
      })
      .filter(Boolean);

    return [...byDeposit, ...byInvoicePaid].sort((a, b) => {
      const rank = { exact: 0, fee_adjusted: 1, close: 2 };
      return (rank[a.matchType] ?? 9) - (rank[b.matchType] ?? 9) || amountDifference(a.bankAmount, a.amount) - amountDifference(b.bankAmount, b.amount);
    });
  };

  // Priority jobs: recurring jobs are one doc, so match on the visit nearest
  // the bank date (occurrence dates from getOccurrencesForJob) and remember
  // which visit via jobOccurrenceDate.
  const findJobMatchesForRow = (row) => {
    const rowDate = toDateKey(row.date);
    if (row.type !== "moneyOut" || !rowDate) return [];
    return bankJobMatchesForDate(jobs, rowDate, Number(matchWindowDays || 5), 12);
  };

  // Value used by the job <select>: "jobId" or "jobId|YYYY-MM-DD" for a recurring visit.
  const jobPickValue = (row = {}) => (row.jobId ? (row.jobOccurrenceDate ? `${row.jobId}|${row.jobOccurrenceDate}` : row.jobId) : "");
  const parseJobPick = (value = "") => {
    const [jobId, occurrenceDate = ""] = String(value || "").split("|");
    return { jobId: jobId || "", occurrenceDate };
  };
  const jobOptionValue = (match) => (match.occurrenceDate ? `${match.jobId}|${match.occurrenceDate}` : match.jobId);

  const moneyInMatchCandidates = useMemo(
    () => findMoneyInMatchesForRow({ ...form, amount, date: entryDate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.type, amount, entryDate, deposits, approvedInvoices, matchWindowDays]
  );

  const jobMatchCandidates = useMemo(
    () => findJobMatchesForRow({ ...form, amount, date: entryDate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.type, amount, entryDate, jobs, matchWindowDays]
  );


  const findExistingDuplicateEntry = (row) =>
    entries.find((entry) => bankEntriesLookDuplicate(row, entry)) || null;

  const findBatchDuplicateRows = (rows) => {
    const seen = new Map();
    const duplicateTempIds = new Set();
    rows.forEach((row) => {
      const key = row.duplicateKey || getBankEntryDuplicateKey(row);
      if (seen.has(key)) {
        duplicateTempIds.add(row.tempId);
      } else {
        seen.set(key, row.tempId);
      }
    });
    return duplicateTempIds;
  };

  const findMonthlyExpenseDuplicate = (row) => {
    if (row.type !== "moneyOut" || row.recurrenceType !== "recurring") return null;
    return monthlyExpenses.find((expense) => monthlyExpenseLooksDuplicate(row, expense)) || null;
  };

  const createMonthlyExpenseFromRow = async (row) => {
    if (row.type !== "moneyOut" || row.recurrenceType !== "recurring") return null;
    if (NO_MONTHLY_CATEGORIES.has(effectiveCategory(row)) || getDefaultAccountingAction(row) === CONTRACTOR_ACTION) {
      return {
        existing: false,
        id: "",
        note: "Not added to monthly expenses: payroll and contractor pay are already counted from job labor / sub pay.",
      };
    }

    const existingMonthly = findMonthlyExpenseDuplicate(row);
    if (existingMonthly) {
      return {
        existing: true,
        id: existingMonthly.id,
        note: `Possible duplicate monthly expense already exists: ${existingMonthly.vendorName || existingMonthly.description || "Monthly Expense"} - ${money(existingMonthly.amount)}.`
      };
    }

    const docRef = await addDoc(collection(db, "monthly_expenses"), {
      vendorId: "",
      vendorName: row.vendorOrPayer || row.description || "Bank Statement Recurring Expense",
      billType: effectiveCategory(row),
      billTypeOther: row.category === "Other" ? row.categoryOther || "" : "",
      description: [row.description || "Recurring bank statement expense", row.referenceNumber ? `Ref ${row.referenceNumber}` : "", row.statementSection || ""].filter(Boolean).join(" • "),
      amount: toNum(row.amount),
      frequency: row.recurringFrequency || "Monthly",
      startDate: row.date || todayKey(),
      source: "BankStatementEntriesRecurringImport",
      sourceCollection: "bankStatementEntries",
      sourceBankStatementDuplicateKey: row.duplicateKey || getBankEntryDuplicateKey(row),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { existing: false, id: docRef.id, note: "Recurring monthly expense created from bank statement import." };
  };


  const isMaterialBankEntry = (entry = {}) => {
    if (entry.type !== "moneyOut") return false;
    const categoryText = String(entry.effectiveCategory || effectiveCategory(entry) || entry.category || "").toLowerCase();
    return categoryText.includes("material");
  };

  // Jobs with a visit on that day (recurring visits included). Returns
  // [{ job, occurrenceDate }] — occurrenceDate is the canonical visit key.
  const getSameDayJobsForMaterial = (dateKey) => {
    if (!dateKey) return [];
    return bankJobsOnDay(jobs, dateKey);
  };

  const syncBankMaterialToEstimatedMaterials = async (entry, bankEntryId) => {
    if (!bankEntryId || !isMaterialBankEntry(entry)) {
      return { synced: false, allocations: [], note: "Not a material bank statement expense." };
    }

    const cleanAmount = toNum(entry.amount);
    const entryDate = toDateKey(entry.date);
    if (!cleanAmount || !entryDate) {
      return { synced: false, allocations: [], note: "Missing material amount or date." };
    }

    let allocationJobs = [];
    const selectedJob = entry.jobId ? jobs.find((job) => job.id === entry.jobId) : null;

    if (selectedJob) {
      allocationJobs = [{ job: selectedJob, occurrenceDate: isRecurring(selectedJob) ? entry.jobOccurrenceDate || "" : "" }];
    } else {
      allocationJobs = getSameDayJobsForMaterial(entryDate);
    }

    if (!allocationJobs.length) {
      return {
        synced: false,
        allocations: [],
        note: "Material bank entry was not matched to a job and no jobs were found on the same date.",
      };
    }

    const splitAmount = Math.round((cleanAmount / allocationJobs.length) * 100) / 100;
    let runningTotal = 0;

    const allocations = allocationJobs.map(({ job, occurrenceDate }, index) => {
      const amount = index === allocationJobs.length - 1
        ? Math.round((cleanAmount - runningTotal) * 100) / 100
        : splitAmount;
      runningTotal += amount;

      return {
        jobId: job.id,
        wo: job.wo || "",
        siteName: job.siteName || job.propertyName || "",
        occurrenceDate: occurrenceDate || "",
        recurring: isRecurring(job),
        amount,
      };
    });

    for (const allocation of allocations) {
      const jobRef = doc(db, "jobs", allocation.jobId);

      await runTransaction(db, async (transaction) => {
        const jobSnap = await transaction.get(jobRef);
        if (!jobSnap.exists()) return;

        const jobData = jobSnap.data() || {};
        const existingAllocations = Array.isArray(jobData.bankMaterialAllocations)
          ? jobData.bankMaterialAllocations
          : [];

        const alreadySynced = existingAllocations.some(
          (item) => item.sourceCollection === "bankStatementEntries" && item.sourceId === bankEntryId
        );

        if (alreadySynced) return;

        const nextAllocation = {
          ...allocation,
          source: "BankStatementEntriesMaterialSync",
          sourceCollection: "bankStatementEntries",
          sourceId: bankEntryId,
          date: entryDate,
          storeName: entry.vendorOrPayer || entry.description || "Bank Statement Material",
          description: entry.description || "",
          duplicateKey: entry.duplicateKey || getBankEntryDuplicateKey(entry),
          createdAt: new Date().toISOString(),
        };

        // A recurring job's estimatedMaterial is the per-visit number for the
        // whole series, so a one-off purchase is NOT added to it (it would be
        // repeated on every visit). It is kept on the visit instead
        // (bankMaterialAllocations[].occurrenceDate). The bank line itself is
        // what counts the money on Profit, once.
        const jobIsRecurring = isRecurring(jobData);
        transaction.update(jobRef, {
          ...(jobIsRecurring ? {} : { estimatedMaterial: toNum(jobData.estimatedMaterial) + allocation.amount }),
          bankMaterialAllocations: [...existingAllocations, { ...nextAllocation, addedToEstimatedMaterial: !jobIsRecurring }],
          updatedAt: serverTimestamp(),
        });
      });
    }

    return {
      synced: true,
      allocations,
      note: selectedJob
        ? isRecurring(selectedJob)
          ? `Material bank entry recorded on the recurring job's ${entry.jobOccurrenceDate || entryDate} visit.`
          : "Material bank entry added to the matched job estimated material."
        : `Material bank entry split across ${allocations.length} job visit(s) scheduled on ${entryDate}.`,
    };
  };

  const reverseBankMaterialFromEstimatedMaterials = async (entry = {}) => {
    if (!entry.id || !Array.isArray(entry.materialAllocations) || !entry.materialAllocations.length) return;

    for (const allocation of entry.materialAllocations) {
      if (!allocation.jobId) continue;
      const jobRef = doc(db, "jobs", allocation.jobId);

      await runTransaction(db, async (transaction) => {
        const jobSnap = await transaction.get(jobRef);
        if (!jobSnap.exists()) return;

        const jobData = jobSnap.data() || {};
        const existingAllocations = Array.isArray(jobData.bankMaterialAllocations)
          ? jobData.bankMaterialAllocations
          : [];

        const remainingAllocations = existingAllocations.filter(
          (item) => !(item.sourceCollection === "bankStatementEntries" && item.sourceId === entry.id)
        );

        const removedItems = existingAllocations.filter(
          (item) => item.sourceCollection === "bankStatementEntries" && item.sourceId === entry.id
        );
        const removedAmount = removedItems
          // Only take back what was actually added to estimatedMaterial.
          .filter((item) => item.addedToEstimatedMaterial !== false)
          .reduce((sum, item) => sum + toNum(item.amount), 0);

        if (!removedItems.length) return;

        transaction.update(jobRef, {
          estimatedMaterial: Math.max(0, toNum(jobData.estimatedMaterial) - removedAmount),
          bankMaterialAllocations: remainingAllocations,
          updatedAt: serverTimestamp(),
        });
      });
    }
  };

  // pickValue = "jobId" or "jobId|YYYY-MM-DD" (recurring visit).
  const fillFromJob = (pickValue) => {
    const { jobId, occurrenceDate } = parseJobPick(pickValue);
    const job = jobs.find((j) => j.id === jobId);
    setSelectedJobMatch(pickValue || "");
    setForm((prev) => ({
      ...prev,
      jobId,
      // Picked from the full job list: tie it to the visit nearest the bank date.
      jobOccurrenceDate: job && isRecurring(job)
        ? occurrenceDate || bankJobMatchesForDate([job], toDateKey(prev.date), 62, 1)[0]?.occurrenceDate || ""
        : "",
      wo: job?.wo || prev.wo || "",
      property: job?.siteName || job?.propertyName || prev.property || "",
    }));
  };

  const handleMoneyInMatchChange = (matchId) => {
    setSelectedMoneyInMatch(matchId);
    const match = moneyInMatchCandidates.find((m) => m.id === matchId);
    if (!match) return;

    setForm((prev) => ({
      ...prev,
      invoiceNumber: match.invoiceNumber || prev.invoiceNumber || "",
      wo: match.wo || prev.wo || "",
      property: match.property || prev.property || "",
      description: prev.description || `Matched existing payment: ${match.description}`,
      matchType: match.matchType || "",
      matchDifference: match.matchDifference || 0,
      matchNote: match.matchNote || "",
      matchedFeeDescription: match.matchedFeeDescription || "",
      matchedFeeAmount: match.matchedFeeAmount || 0,
    }));
  };

  const updateImportRow = (tempId, changes) => {
    setImportRows((prev) => prev.map((row) => (row.tempId === tempId ? { ...row, ...changes } : row)));
  };

  const handleImportRowMatchChange = (tempId, matchId) => {
    const row = importRows.find((r) => r.tempId === tempId);
    const match = findMoneyInMatchesForRow(row).find((m) => m.id === matchId);

    if (!matchId || !match) {
      updateImportRow(tempId, {
        matchedExistingPayment: false,
        matchedCollection: "",
        matchedSourceId: "",
        matchedInvoiceNumber: "",
        matchedExistingAmount: 0,
        matchedExistingDate: "",
        matchType: "",
        matchDifference: 0,
        matchNote: "",
        matchedFeeDescription: "",
        matchedFeeAmount: 0,
        accountingAction: row.type === "moneyIn" ? "count_as_income" : "count_as_expense",
      });
      return;
    }

    updateImportRow(tempId, {
      matchedExistingPayment: true,
      matchedCollection: match.collection,
      matchedSourceId: match.sourceId,
      matchedInvoiceNumber: match.invoiceNumber || "",
      matchedExistingAmount: match.amount || 0,
      matchedExistingDate: match.date || "",
      matchType: match.matchType || "",
      matchDifference: match.matchDifference || 0,
      matchNote: match.matchNote || "",
      matchedFeeDescription: match.matchedFeeDescription || "",
      matchedFeeAmount: match.matchedFeeAmount || 0,
      invoiceNumber: match.invoiceNumber || row.invoiceNumber || "",
      wo: match.wo || row.wo || "",
      property: match.property || row.property || "",
      accountingAction: "matched_existing_do_not_count",
      status: "Matched",
    });
  };

  const handleImportRowJobChange = (tempId, pickValue) => {
    const { jobId, occurrenceDate } = parseJobPick(pickValue);
    const job = jobs.find((j) => j.id === jobId);
    updateImportRow(tempId, {
      jobId: jobId || "",
      jobOccurrenceDate: job && isRecurring(job) ? occurrenceDate : "",
      wo: job?.wo || "",
      property: job?.siteName || job?.propertyName || "",
      status: jobId ? "Matched" : "Needs Review",
    });
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      alert("Please upload a PDF bank statement.");
      return;
    }

    setImportingPdf(true);
    setPdfFileName(file.name);

    try {
      const text = await readPdfText(file);
      setRawExtractedText(text);
      const parsed = parseStatementText(text, file.name).map((row) => {
        const rowWithBank = applyBankAccountToEntry(row, selectedUploadBankAccount);
        return {
        ...rowWithBank,
        duplicateKey: getBankEntryDuplicateKey(rowWithBank),
        duplicateWarning: findExistingDuplicateEntry(rowWithBank) ? "Already imported - duplicate" : "",
        monthlyDuplicateWarning: findMonthlyExpenseDuplicate(rowWithBank) ? "Possible recurring monthly duplicate" : "",
      };
      });
      setImportRows(applyAutoContractorMatches(parsed));

      if (!parsed.length) {
        alert("I could not find transaction rows in this PDF. Try a text-based bank PDF, or copy/paste the statement text into the raw text box after extraction.");
      }
    } catch (err) {
      console.error("PDF import failed:", err);
      alert(`Could not read this PDF. Details: ${err?.message || err}. If this keeps happening, use "Show / Paste Raw Statement Text" and paste the statement text instead.`);
    } finally {
      setImportingPdf(false);
      event.target.value = "";
    }
  };

  const reparseRawText = () => {
    const parsed = parseStatementText(rawExtractedText, pdfFileName || "Pasted Statement Text").map((row) => {
      const rowWithBank = applyBankAccountToEntry(row, selectedUploadBankAccount);
      return {
      ...rowWithBank,
      duplicateKey: getBankEntryDuplicateKey(rowWithBank),
      duplicateWarning: findExistingDuplicateEntry(rowWithBank) ? "Already imported - duplicate" : "",
      monthlyDuplicateWarning: findMonthlyExpenseDuplicate(rowWithBank) ? "Possible recurring monthly duplicate" : "",
    };
    });
    setImportRows(applyAutoContractorMatches(parsed));
  };

  const syncApprovedInvoicePaidStatus = async (invoiceNumber) => {
    const invoiceKey = String(invoiceNumber || "").trim();
    if (!invoiceKey) return;

    const [depositsSnap, approvedInvoicesSnap] = await Promise.all([
      getDocs(collection(db, "deposits")),
      getDocs(collection(db, "approvedInvoices")),
    ]);

    const totalPaid = depositsSnap.docs.reduce((sum, d) => {
      const deposit = d.data();
      const depositInvoiceNumber = String(deposit.invoiceNumber || "").trim();
      if (depositInvoiceNumber !== invoiceKey) return sum;
      return sum + toNum(deposit.amount);
    }, 0);

    const matchingApprovedInvoices = approvedInvoicesSnap.docs.filter((d) => {
      const invoice = d.data();
      return getApprovedInvoiceNumber(invoice) === invoiceKey;
    });

    for (const invoiceDoc of matchingApprovedInvoices) {
      const invoice = invoiceDoc.data();
      const invoiceTotal = toNum(invoice.amount || invoice.rate);
      if (!invoiceTotal) continue;

      if (totalPaid >= invoiceTotal) {
        await updateDoc(doc(db, "approvedInvoices", invoiceDoc.id), {
          status: "Paid",
          paidAt: new Date().toISOString(),
          paidAmount: totalPaid,
          paymentMatchedFrom: "bankStatementEntries",
          updatedAt: new Date().toISOString(),
        });
      } else if (invoice.status === "Paid") {
        await updateDoc(doc(db, "approvedInvoices", invoiceDoc.id), {
          status: "Sent",
          paidAmount: totalPaid,
          paymentMatchedFrom: "bankStatementEntries",
          updatedAt: new Date().toISOString(),
        });
      }
    }
  };

  const saveCleanEntry = async (entry, existingMatch = null) => {
    const cleanAmount = toNum(entry.amount);
    const cleanEntry = {
      ...entry,
      amount: cleanAmount,
      date: entry.date || todayKey(),
      transactionType: entry.type === "moneyIn" ? "income" : "expense",
      effectiveCategory: effectiveCategory(entry),
      matchedExistingPayment: !!existingMatch || !!entry.matchedExistingPayment,
      matchedCollection: existingMatch?.collection || entry.matchedCollection || "",
      matchedSourceId: existingMatch?.sourceId || entry.matchedSourceId || "",
      matchedInvoiceNumber: existingMatch?.invoiceNumber || entry.matchedInvoiceNumber || "",
      matchedExistingAmount: existingMatch?.amount || entry.matchedExistingAmount || 0,
      matchedExistingDate: existingMatch?.date || entry.matchedExistingDate || "",
      matchType: existingMatch?.matchType || entry.matchType || "",
      matchDifference: existingMatch?.matchDifference || entry.matchDifference || 0,
      matchNote: existingMatch?.matchNote || entry.matchNote || "",
      matchedFeeDescription: existingMatch?.matchedFeeDescription || entry.matchedFeeDescription || "",
      matchedFeeAmount: existingMatch?.matchedFeeAmount || entry.matchedFeeAmount || 0,
      bankAccountId: entry.bankAccountId || "",
      bankName: entry.bankName || "",
      accountLast4: String(entry.accountLast4 || "").replace(/\D/g, "").slice(-4),
      accountType: entry.accountType || "operating",
      bankAccountLabel: bankAccountLabel(entry),
      referenceNumber: entry.referenceNumber || "",
      statementSection: entry.statementSection || "",
      statementPeriod: entry.statementPeriod || "",
      transactionDetail: entry.transactionDetail || "",
      rawLine: entry.rawLine || "",
      accountingAction: existingMatch || entry.matchedExistingPayment ? "matched_existing_do_not_count" : getDefaultAccountingAction(entry),
      recurrenceType: entry.recurrenceType || "oneTime",
      recurringFrequency: entry.recurringFrequency || "Monthly",
      isRecurring: entry.recurrenceType === "recurring",
      duplicateKey: entry.duplicateKey || getBankEntryDuplicateKey(entry),
      duplicateCheckedAt: new Date().toISOString(),
      monthlyExpenseId: entry.monthlyExpenseId || "",
      monthlyExpenseDuplicateId: entry.monthlyExpenseDuplicateId || "",
      monthlyExpenseNote: entry.monthlyExpenseNote || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    delete cleanEntry.tempId;
    delete cleanEntry.selected;
    delete cleanEntry.duplicateWarning;
    delete cleanEntry.monthlyDuplicateWarning;

    const entryRef = await addDoc(collection(db, "bankStatementEntries"), cleanEntry);

    if (!accountingActionCounts(cleanEntry)) {
      await updateDoc(entryRef, {
        auditNote:
          getDefaultAccountingAction(cleanEntry) === CONTRACTOR_ACTION
            ? `Matched to contractor payment for ${cleanEntry.matchedContractorName || "a contractor"}. Counted once on Profit; no second ledger entry because the contractor payment already has one.`
            : `${accountingActionLabel(cleanEntry)}. This bank statement entry is saved for recordkeeping only and is not included in income, expense, job profit, deposits, or monthly expense totals.`,
        updatedAt: serverTimestamp(),
      });
      return entryRef.id;
    }

    if (cleanEntry.type === "moneyOut" && cleanEntry.recurrenceType === "recurring") {
      const monthlyResult = await createMonthlyExpenseFromRow(cleanEntry);
      if (monthlyResult) {
        await updateDoc(entryRef, {
          monthlyExpenseId: monthlyResult.existing ? "" : monthlyResult.id,
          monthlyExpenseDuplicateId: monthlyResult.existing ? monthlyResult.id : "",
          monthlyExpenseNote: monthlyResult.note,
          updatedAt: serverTimestamp(),
        });
      }
    }

    const materialSyncResult = await syncBankMaterialToEstimatedMaterials(cleanEntry, entryRef.id);
    if (materialSyncResult.synced || materialSyncResult.note) {
      await updateDoc(entryRef, {
        materialSyncStatus: materialSyncResult.synced ? "synced_to_estimated_material" : "not_synced",
        materialSyncNote: materialSyncResult.note || "",
        materialAllocations: materialSyncResult.allocations || [],
        updatedAt: serverTimestamp(),
      });
    }

    const txRef = await addDoc(collection(db, "transactions"), {
      date: cleanEntry.date,
      type: cleanEntry.type === "moneyIn" ? "income" : "expense",
      amount: cleanAmount,
      category: cleanEntry.effectiveCategory || cleanEntry.category || (cleanEntry.type === "moneyIn" ? "Bank Deposit" : "Bank Expense"),
      vendor: cleanEntry.type === "moneyOut" ? cleanEntry.vendorOrPayer : "",
      payer: cleanEntry.type === "moneyIn" ? cleanEntry.vendorOrPayer : "",
      memo: [cleanEntry.description || "Bank statement entry", cleanEntry.referenceNumber ? `Ref ${cleanEntry.referenceNumber}` : "", cleanEntry.matchNote || ""].filter(Boolean).join(" • "),
      jobId: cleanEntry.jobId || "",
      jobOccurrenceDate: cleanEntry.jobOccurrenceDate || "",
      wo: cleanEntry.wo || "",
      invoiceNumber: cleanEntry.invoiceNumber || "",
      company: cleanEntry.company || "",
      bankAccountId: cleanEntry.bankAccountId || "",
      bankName: cleanEntry.bankName || "",
      accountLast4: cleanEntry.accountLast4 || "",
      accountType: cleanEntry.accountType || "operating",
      bankAccountLabel: cleanEntry.bankAccountLabel || bankAccountLabel(cleanEntry),
      accountingAction: cleanEntry.accountingAction || getDefaultAccountingAction(cleanEntry),
      accountingActionLabel: accountingActionLabel(cleanEntry),
      referenceNumber: cleanEntry.referenceNumber || "",
      statementSection: cleanEntry.statementSection || "",
      statementPeriod: cleanEntry.statementPeriod || "",
      matchNote: cleanEntry.matchNote || "",
      matchedFeeAmount: cleanEntry.matchedFeeAmount || 0,
      matchedFeeDescription: cleanEntry.matchedFeeDescription || "",
      source: "bankStatementEntries",
      sourceCollection: "bankStatementEntries",
      sourceId: entryRef.id,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(entryRef, { transactionId: txRef.id });

    if (BANK_MONEY_IN_CREATES_DEPOSITS && cleanEntry.type === "moneyIn" && cleanEntry.invoiceNumber) {
      await addDoc(collection(db, "deposits"), {
        property: cleanEntry.property || "",
        amount: cleanAmount,
        checkNumber: cleanEntry.bankName || "Bank Statement",
        invoiceNumber: cleanEntry.invoiceNumber,
        invoiceNumber2: cleanEntry.wo || "",
        description: [cleanEntry.description || "Bank statement money in", cleanEntry.referenceNumber ? `Ref ${cleanEntry.referenceNumber}` : ""].filter(Boolean).join(" • "),
        company: cleanEntry.company || "",
        date: cleanEntry.date,
        bankAccountId: cleanEntry.bankAccountId || "",
        bankName: cleanEntry.bankName || "",
        accountLast4: cleanEntry.accountLast4 || "",
        bankAccountLabel: cleanEntry.bankAccountLabel || bankAccountLabel(cleanEntry),
        referenceNumber: cleanEntry.referenceNumber || "",
        statementSection: cleanEntry.statementSection || "",
        statementPeriod: cleanEntry.statementPeriod || "",
        source: "bankStatementEntries",
        sourceCollection: "bankStatementEntries",
        sourceId: entryRef.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await syncApprovedInvoicePaidStatus(cleanEntry.invoiceNumber);
    }
    return entryRef.id;
  };



  const saveBankAccount = async (e) => {
    e.preventDefault();
    const bankName = String(bankAccountForm.bankName || "").trim();
    const accountLast4 = String(bankAccountForm.accountLast4 || "").replace(/\D/g, "").slice(-4);
    const nickname = String(bankAccountForm.nickname || "").trim();
    const accountType = bankAccountForm.accountType || "operating";

    if (!bankName) {
      alert("Enter a bank name.");
      return;
    }
    if (accountLast4.length !== 4) {
      alert("Enter the last 4 digits of the account.");
      return;
    }

    const exists = bankAccounts.some(
      (account) =>
        String(account.bankName || "").trim().toLowerCase() === bankName.toLowerCase() &&
        String(account.accountLast4 || "").replace(/\D/g, "").slice(-4) === accountLast4
    );
    if (exists) {
      alert("That bank and last 4 are already saved.");
      return;
    }

    setSavingBankAccount(true);
    try {
      await addDoc(collection(db, "bankStatementAccounts"), {
        bankName,
        accountLast4,
        nickname,
        accountType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setBankAccountForm(emptyBankAccount);
      await loadData();
    } catch (err) {
      console.error("Error saving bank account:", err);
      alert("Could not save bank/account.");
    } finally {
      setSavingBankAccount(false);
    }
  };

  const startEditEntry = (entry) => {
    setEditingEntryId(entry.id);
    setEditEntryForm({
      ...entry,
      date: toDateKey(entry.date) || todayKey(),
      amount: toNum(entry.amount),
      bankAccountId: entry.bankAccountId || "",
      bankName: entry.bankName || "",
      accountLast4: entry.accountLast4 || "",
      category: entry.category || "Other",
      categoryOther: entry.categoryOther || "",
      recurrenceType: entry.recurrenceType || "oneTime",
      recurringFrequency: entry.recurringFrequency || "Monthly",
    });
  };

  const cancelEditEntry = () => {
    setEditingEntryId("");
    setEditEntryForm(null);
  };

  const updateRelatedDocsForEditedEntry = async (entryId, cleanEntry) => {
    if (!accountingActionCounts(cleanEntry)) {
      await Promise.all([
        deleteRelatedDocsBySourceId("transactions", entryId),
        deleteRelatedDocsBySourceId("deposits", entryId),
      ]);
      return;
    }

    const relatedPayload = {
      date: cleanEntry.date,
      amount: cleanEntry.amount,
      type: cleanEntry.type === "moneyIn" ? "income" : "expense",
      category: cleanEntry.effectiveCategory || cleanEntry.category || "Other",
      vendor: cleanEntry.type === "moneyOut" ? cleanEntry.vendorOrPayer || "" : "",
      payer: cleanEntry.type === "moneyIn" ? cleanEntry.vendorOrPayer || "" : "",
      memo: [cleanEntry.description || "Bank statement entry", cleanEntry.referenceNumber ? `Ref ${cleanEntry.referenceNumber}` : "", cleanEntry.matchNote || ""].filter(Boolean).join(" • "),
      jobId: cleanEntry.jobId || "",
      jobOccurrenceDate: cleanEntry.jobOccurrenceDate || "",
      wo: cleanEntry.wo || "",
      invoiceNumber: cleanEntry.invoiceNumber || "",
      company: cleanEntry.company || "",
      bankAccountId: cleanEntry.bankAccountId || "",
      bankName: cleanEntry.bankName || "",
      accountLast4: cleanEntry.accountLast4 || "",
      accountType: cleanEntry.accountType || "operating",
      bankAccountLabel: cleanEntry.bankAccountLabel || bankAccountLabel(cleanEntry),
      accountingAction: cleanEntry.accountingAction || getDefaultAccountingAction(cleanEntry),
      accountingActionLabel: accountingActionLabel(cleanEntry),
      referenceNumber: cleanEntry.referenceNumber || "",
      statementSection: cleanEntry.statementSection || "",
      statementPeriod: cleanEntry.statementPeriod || "",
      updatedAt: serverTimestamp(),
    };

    const txSnap = await getDocs(query(collection(db, "transactions"), where("sourceId", "==", entryId)));
    await Promise.all(txSnap.docs.map((d) => updateDoc(doc(db, "transactions", d.id), relatedPayload)));

    const depositSnap = await getDocs(query(collection(db, "deposits"), where("sourceId", "==", entryId)));
    await Promise.all(depositSnap.docs.map((d) => updateDoc(doc(db, "deposits", d.id), {
      property: cleanEntry.property || "",
      amount: cleanEntry.amount,
      checkNumber: cleanEntry.bankName || "Bank Statement",
      invoiceNumber: cleanEntry.invoiceNumber || cleanEntry.matchedInvoiceNumber || "",
      invoiceNumber2: cleanEntry.wo || "",
      description: [cleanEntry.description || "Bank statement money in", cleanEntry.referenceNumber ? `Ref ${cleanEntry.referenceNumber}` : ""].filter(Boolean).join(" • "),
      company: cleanEntry.company || "",
      date: cleanEntry.date,
      bankAccountId: cleanEntry.bankAccountId || "",
      bankName: cleanEntry.bankName || "",
      accountLast4: cleanEntry.accountLast4 || "",
      accountType: cleanEntry.accountType || "operating",
      bankAccountLabel: cleanEntry.bankAccountLabel || bankAccountLabel(cleanEntry),
      accountingAction: cleanEntry.accountingAction || getDefaultAccountingAction(cleanEntry),
      accountingActionLabel: accountingActionLabel(cleanEntry),
      referenceNumber: cleanEntry.referenceNumber || "",
      statementSection: cleanEntry.statementSection || "",
      statementPeriod: cleanEntry.statementPeriod || "",
      updatedAt: serverTimestamp(),
    })));
  };

  const saveEditedEntry = async () => {
    if (!editingEntryId || !editEntryForm) return;
    const cleanAmount = toNum(editEntryForm.amount);
    if (!cleanAmount || cleanAmount <= 0) {
      alert("Enter a valid amount.");
      return;
    }

    const cleanEntry = {
      ...editEntryForm,
      amount: cleanAmount,
      accountLast4: String(editEntryForm.accountLast4 || "").replace(/\D/g, "").slice(-4),
      effectiveCategory: effectiveCategory(editEntryForm),
      accountType: editEntryForm.accountType || "operating",
      bankAccountLabel: bankAccountLabel(editEntryForm),
      accountingAction: getDefaultAccountingAction(editEntryForm),
      transactionType: editEntryForm.type === "moneyIn" ? "income" : "expense",
      isRecurring: editEntryForm.recurrenceType === "recurring",
      duplicateKey: getBankEntryDuplicateKey(editEntryForm),
      updatedAt: serverTimestamp(),
    };

    const duplicateEntry = entries.find((entry) => entry.id !== editingEntryId && bankEntriesLookDuplicate(cleanEntry, entry));
    if (duplicateEntry) {
      alert("This edit appears to duplicate another saved bank statement entry. It was not saved.");
      return;
    }

    setSaving(true);
    try {
      const entryRef = doc(db, "bankStatementEntries", editingEntryId);
      const payload = { ...cleanEntry };
      delete payload.id;
      await updateDoc(entryRef, payload);
      await updateRelatedDocsForEditedEntry(editingEntryId, cleanEntry);
      const before = entries.find((e) => e.id === editingEntryId) || {};
      await handleCountingChange(before, cleanEntry, editingEntryId);
      cancelEditEntry();
      await loadData();
    } catch (err) {
      console.error("Error updating bank statement entry:", err);
      alert("Could not update this bank statement entry.");
    } finally {
      setSaving(false);
    }
  };

  const deleteRelatedDocsBySourceId = async (collectionName, sourceId) => {
    const snap = await getDocs(
      query(collection(db, collectionName), where("sourceId", "==", sourceId))
    );
    await Promise.all(snap.docs.map((d) => deleteDoc(doc(db, collectionName, d.id))));
  };

  const deleteBankStatementEntry = async (entry) => {
    if (!entry?.id) return;

    const confirmText = [
      "Delete this bank statement entry?",
      "",
      `${entry.date || "No date"} - ${entry.type === "moneyIn" ? "Money In" : "Money Out"} - ${money(entry.amount)}`,
      entry.vendorOrPayer ? `Vendor/Payer: ${entry.vendorOrPayer}` : "",
      entry.description ? `Description: ${entry.description}` : "",
      "",
      "This will also remove transactions/deposits created from this bank statement entry so it does not keep calculating after the entry is deleted.",
      entry.monthlyExpenseId ? "This will also remove the monthly recurring expense that was created from this entry." : "",
    ].filter(Boolean).join("\n");

    if (!window.confirm(confirmText)) return;

    try {
      setSaving(true);

      await reverseBankMaterialFromEstimatedMaterials(entry);

      await Promise.all([
        entry.transactionId ? deleteDoc(doc(db, "transactions", entry.transactionId)).catch(() => null) : Promise.resolve(),
        deleteRelatedDocsBySourceId("transactions", entry.id),
        deleteRelatedDocsBySourceId("deposits", entry.id),
        entry.monthlyExpenseId ? deleteDoc(doc(db, "monthly_expenses", entry.monthlyExpenseId)).catch(() => null) : Promise.resolve(),
      ]);

      if (entry.matchedContractorPayId) {
        await markContractorPayment(entry.matchedContractorPayCollection, entry.matchedContractorPayId, null);
      }
      await deleteDoc(doc(db, "bankStatementEntries", entry.id));
      await loadData();
    } catch (err) {
      console.error("Error deleting bank statement entry:", err);
      alert("Could not delete this bank statement entry.");
    } finally {
      setSaving(false);
    }
  };

  const saveEntry = async (e) => {
    e.preventDefault();

    const cleanAmount = toNum(form.amount);
    if (!cleanAmount || cleanAmount <= 0) {
      alert("Enter a valid amount.");
      return;
    }

    const duplicateEntry = findExistingDuplicateEntry({
      ...form,
      amount: cleanAmount,
      duplicateKey: form.duplicateKey || getBankEntryDuplicateKey(form),
    });

    if (duplicateEntry) {
      alert("This bank statement entry already appears to be imported. It was not saved again.");
      return;
    }

    setSaving(true);
    try {
      const existingMatch = moneyInMatchCandidates.find((m) => m.id === selectedMoneyInMatch);
      await saveCleanEntry(form, existingMatch || null);

      setForm(emptyEntry);
      setSelectedMoneyInMatch("");
      setSelectedJobMatch("");
      await loadData();
    } catch (err) {
      console.error("Error saving bank statement entry:", err);
      alert("Could not save bank statement entry.");
    } finally {
      setSaving(false);
    }
  };

  const importSelectedRows = async () => {
    const selected = importRows.filter((row) => row.selected && toNum(row.amount) > 0 && row.date);
    if (!selected.length) {
      alert("Select at least one valid row to import.");
      return;
    }

    const batchDuplicates = findBatchDuplicateRows(selected);
    const existingDuplicates = selected.filter((row) => findExistingDuplicateEntry(row));
    const monthlyDuplicates = selected.filter((row) => findMonthlyExpenseDuplicate(row));
    const rowsToImport = selected.filter((row) => !batchDuplicates.has(row.tempId) && !findExistingDuplicateEntry(row));

    if (!rowsToImport.length) {
      alert("All selected rows appear to be duplicates already imported, so nothing new was saved.");
      setImportRows((prev) => prev.map((row) => selected.some((s) => s.tempId === row.tempId) ? { ...row, selected: false, duplicateWarning: "Already imported or duplicate selected row" } : row));
      return;
    }

    const duplicateText = [];
    if (existingDuplicates.length) duplicateText.push(`${existingDuplicates.length} row(s) already exist and will be skipped`);
    if (batchDuplicates.size) duplicateText.push(`${batchDuplicates.size} duplicate row(s) inside this selection will be skipped`);
    if (monthlyDuplicates.length) duplicateText.push(`${monthlyDuplicates.length} recurring row(s) may match an existing monthly expense; the bank row will still import, but a duplicate monthly expense will not be created`);

    const confirmed = window.confirm(
      `Import ${rowsToImport.length} new selected bank statement rows?\n\n${duplicateText.length ? duplicateText.join("\n") + "\n\n" : ""}Matched money-in rows will not be counted twice. One-time is the default. Recurring rows will create or match monthly expenses. Material money-out rows will update estimated materials: matched rows go to the selected job, unmatched rows split across jobs on the same date.`
    );
    if (!confirmed) return;

    setImportingRows(true);
    try {
      for (const row of rowsToImport) {
        const existingMonthly = findMonthlyExpenseDuplicate(row);
        const savedId = await saveCleanEntry({
          ...row,
          duplicateKey: row.duplicateKey || getBankEntryDuplicateKey(row),
          monthlyExpenseDuplicateId: existingMonthly?.id || "",
          monthlyExpenseNote: existingMonthly ? `Possible duplicate monthly expense already exists: ${existingMonthly.vendorName || existingMonthly.description || "Monthly Expense"} - ${money(existingMonthly.amount)}.` : "",
        }, null);
        if (savedId && row.matchedContractorPayId) {
          await markContractorPayment(row.matchedContractorPayCollection, row.matchedContractorPayId, savedId, row);
        }
      }
      setImportRows((prev) => prev.filter((row) => !rowsToImport.some((saved) => saved.tempId === row.tempId)).map((row) => ({
        ...row,
        selected: false,
        duplicateWarning: selected.some((s) => s.tempId === row.tempId) ? "Skipped as duplicate" : row.duplicateWarning,
      })));
      setRawExtractedText("");
      setPdfFileName("");
      await loadData();
      alert(`Imported ${rowsToImport.length} row(s). Skipped ${selected.length - rowsToImport.length} duplicate row(s).`);
    } catch (err) {
      console.error("Error importing statement rows:", err);
      alert("Could not import selected rows. Check console for details.");
    } finally {
      setImportingRows(false);
    }
  };

  /* ================================================================== */
  /* Personal money out                                                 */
  /* ================================================================== */

  // Personal types = the "Owner draw (personal)" categories, including any you add.
  const personalTypes = useMemo(
    () => [REPAYMENT_TYPE, ...mergeCategories(customCats).filter((c) => c.kind === "owner_draw").map((c) => c.name)],
    [customCats]
  );

  /** Mark / unmark a row as personal. Personal rows are saved as owner draws, so
   * they are NOT counted as a business expense and never create a transaction. */
  const personalChanges = (row, on, type) => {
    if (on) {
      const next = getCategoryDrivenEntryChanges(row, "Personal / Owner Draw");
      return { ...next, personalType: type || row.personalType || "Personal", duplicateKey: getBankEntryDuplicateKey(next) };
    }
    const guessed = guessCategory(`${row.statementSection || ""} ${row.description || ""}`, row.type, row.amount);
    const category = guessed === "Personal / Owner Draw" || guessed === "Personal Life Insurance" ? "Other" : guessed;
    const next = getCategoryDrivenEntryChanges({ ...row, type: "moneyOut" }, category);
    return { ...next, type: "moneyOut", personalType: "", duplicateKey: getBankEntryDuplicateKey(next) };
  };

  /** Mark / unmark a money-in line as reimbursable (has to be paid back). */
  const reimbursableChanges = (row, on, extra = {}) =>
    on
      ? {
          ...row,
          reimbursable: true,
          reimbursableFrom: row.reimbursableFrom || "owner",
          reimbursableNote: row.reimbursableNote || "",
          matchedExistingPayment: false,
          accountingAction: REIMBURSABLE_ACTION,
          ...extra,
        }
      : {
          ...row,
          reimbursable: false,
          reimbursableFrom: "",
          reimbursableNote: "",
          accountingAction: row.accountType === "creditCard" ? "credit_card_payment_transfer" : "count_as_income",
          ...extra,
        };

  const renderReimbursableFields = (row, onChange) => (
    <div className="bse-reimb-fields">
      <select value={row.reimbursableFrom || "owner"} onChange={(e) => onChange({ reimbursableFrom: e.target.value })}>
        <option value="owner">From owner (personal money)</option>
        <option value="other">Loan / other</option>
      </select>
      <input
        value={row.reimbursableNote || ""}
        onChange={(e) => onChange({ reimbursableNote: e.target.value })}
        placeholder="Note: who, why, how it gets paid back"
      />
    </div>
  );

  /**
   * After a saved line changes between "counts" and "doesn't count" (e.g. it
   * was switched to Personal), keep the linked records in step.
   */
  const handleCountingChange = async (before, after, entryId) => {
    const wasCounted = accountingActionCounts(before);
    const nowCounted = accountingActionCounts(after);
    if (wasCounted && !nowCounted) {
      await reverseBankMaterialFromEstimatedMaterials({ ...before, id: entryId });
      if (before.materialAllocations?.length) {
        await updateDoc(doc(db, "bankStatementEntries", entryId), { materialAllocations: [], materialSyncStatus: "reversed" });
      }
    }
    if (!wasCounted && nowCounted) {
      const txSnap = await getDocs(query(collection(db, "transactions"), where("sourceId", "==", entryId)));
      if (txSnap.empty) {
        const txRef = await addDoc(collection(db, "transactions"), {
          date: after.date,
          type: after.type === "moneyIn" ? "income" : "expense",
          amount: toNum(after.amount),
          category: effectiveCategory(after),
          vendor: after.type === "moneyOut" ? after.vendorOrPayer || "" : "",
          payer: after.type === "moneyIn" ? after.vendorOrPayer || "" : "",
          memo: [after.description || "Bank statement entry", after.referenceNumber ? `Ref ${after.referenceNumber}` : ""].filter(Boolean).join(" • "),
          jobId: after.jobId || "",
          jobOccurrenceDate: after.jobOccurrenceDate || "",
          bankAccountId: after.bankAccountId || "",
          bankAccountLabel: bankAccountLabel(after),
          accountingAction: getDefaultAccountingAction(after),
          accountingActionLabel: accountingActionLabel(after),
          source: "bankStatementEntries",
          sourceCollection: "bankStatementEntries",
          sourceId: entryId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await updateDoc(doc(db, "bankStatementEntries", entryId), { transactionId: txRef.id });
      }
      if (after.type === "moneyOut") {
        const result = await syncBankMaterialToEstimatedMaterials(after, entryId);
        if (result?.synced) {
          await updateDoc(doc(db, "bankStatementEntries", entryId), {
            materialSyncStatus: "synced_to_estimated_material",
            materialAllocations: result.allocations || [],
          });
        }
      }
    }
  };

  /** Save a change to a line straight from the saved list (Personal / Reimbursable ticks). */
  const quickUpdateEntry = async (entry, changes) => {
    const merged = { ...entry, ...changes };
    const cleanEntry = {
      ...merged,
      effectiveCategory: effectiveCategory(merged),
      accountingAction: getDefaultAccountingAction(merged),
      bankAccountLabel: bankAccountLabel(merged),
      transactionType: merged.type === "moneyIn" ? "income" : "expense",
      duplicateKey: getBankEntryDuplicateKey(merged),
    };
    const payload = { ...cleanEntry, updatedAt: serverTimestamp() };
    delete payload.id;
    setSaving(true);
    try {
      await updateDoc(doc(db, "bankStatementEntries", entry.id), payload);
      await updateRelatedDocsForEditedEntry(entry.id, cleanEntry);
      await handleCountingChange(entry, cleanEntry, entry.id);
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, ...cleanEntry } : e)));
    } catch (err) {
      console.error("Error updating bank statement entry:", err);
      alert("Could not update this line.");
    } finally {
      setSaving(false);
    }
  };

  /* ================================================================== */
  /* Contractor pay matching                                            */
  /* ================================================================== */

  /** Mark (or un-mark) a contractor payment as found on a bank statement. */
  const markContractorPayment = async (collectionName, payId, bankEntryId, line = {}) => {
    if (!payId) return;
    const changes = bankEntryId
      ? {
          bankVerified: true,
          bankEntryId,
          bankVerifiedAt: new Date().toISOString(),
          bankLineDate: toDateKey(line.date),
          bankLineAmount: toNum(line.amount),
          bankAccountLabel: bankAccountLabel(line),
        }
      : { bankVerified: false, bankEntryId: "", bankVerifiedAt: "", bankLineDate: "", bankLineAmount: 0, bankAccountLabel: "" };
    try {
      await updateDoc(doc(db, collectionName || "subcontractorPay", payId), changes);
      setContractorPays((prev) => prev.map((p) => (p.id === payId ? { ...p, bankVerified: !!bankEntryId, bankEntryId: bankEntryId || "" } : p)));
    } catch (err) {
      console.error("Could not update the contractor payment:", err);
    }
  };

  /** Row changes to link a line to a contractor payment (or undo the link). */
  const contractorChanges = (row, pay, mode = "manual") => {
    if (pay) {
      return {
        matchedContractorPayId: pay.id,
        matchedContractorPayCollection: pay.sourceCollection,
        matchedContractorName: pay.name,
        contractorMatchMode: mode,
        category: "Subcontractor",
        categoryOther: "",
        personalType: "",
        accountingAction: CONTRACTOR_ACTION,
        recurrenceType: "oneTime",
        jobId: row.jobId || "",
      };
    }
    return {
      matchedContractorPayId: "",
      matchedContractorPayCollection: "",
      matchedContractorName: "",
      contractorMatchMode: "none",
      accountingAction: "count_as_expense",
    };
  };

  /** Payment ids already taken by other rows in this review (so one payment isn't used twice). */
  const claimedIds = (exceptTempId) =>
    new Set(importRows.filter((r) => r.tempId !== exceptTempId && r.matchedContractorPayId).map((r) => r.matchedContractorPayId));

  /** Link each new money-out row to a contractor payment when one clearly fits. */
  const applyAutoContractorMatches = (rows) => {
    const taken = new Set();
    return rows.map((row) => {
      if (row.type === "moneyIn" || entryIsPersonal(row) || row.contractorMatchMode === "manual" || row.contractorMatchMode === "none") return row;
      const best = autoPick(contractorCandidates(row, contractorPays, taken));
      if (!best) return row;
      taken.add(best.pay.id);
      return { ...row, ...contractorChanges(row, best.pay, "auto"), contractorMatchReasons: best.fit.reasons.join(", ") };
    });
  };

  const payLabel = (p) =>
    [p.name, money(p.amount), p.date, p.method, p.reference ? `#${p.reference}` : "", p.note ? `“${p.note.slice(0, 30)}”` : ""]
      .filter(Boolean)
      .join(" · ");

  /** The contractor picker used in the review table and on saved lines. */
  const renderContractorPicker = (line, claimed, onPick) => {
    const best = contractorCandidates(line, contractorPays, claimed);
    const bestIds = new Set(best.map((b) => b.pay.id));
    const others = nearbyPayments(line, contractorPays, claimed).filter((x) => !bestIds.has(x.pay.id)).slice(0, 25);
    const current = contractorPays.find((p) => p.id === line.matchedContractorPayId);
    return (
      <select
        value={line.matchedContractorPayId || ""}
        onChange={(e) => onPick(contractorPays.find((p) => p.id === e.target.value) || null)}
        className="bse-contractor-select"
      >
        <option value="">— Not contractor pay —</option>
        {current && !bestIds.has(current.id) && !others.some((o) => o.pay.id === current.id) && (
          <option value={current.id}>{payLabel(current)}</option>
        )}
        {best.length > 0 && (
          <optgroup label="Same amount (best matches)">
            {best.map(({ pay }) => <option key={pay.id} value={pay.id}>{payLabel(pay)}</option>)}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label="Other contractor payments near this date">
            {others.map(({ pay }) => <option key={pay.id} value={pay.id}>{payLabel(pay)}</option>)}
          </optgroup>
        )}
      </select>
    );
  };

  const renderContractorCell = (line, { claimed, onPick, compact = false }) => {
    if (line.type === "moneyIn" || entryIsPersonal(line)) return <span className="fin-muted">—</span>;
    const matched = !!line.matchedContractorPayId;
    const payroll = looksLikePayroll(line);
    const hasCandidates = contractorCandidates(line, contractorPays, claimed).length > 0;
    const showPicker = matched || hasCandidates || looksLikeContractorPay(line) || line.showContractorPicker;
    return (
      <div className="bse-contractor">
        {matched && (
          <div className="bse-contractor__ok">
            ✓ Contractor pay: <b>{line.matchedContractorName}</b>
            {line.contractorMatchMode === "auto" && <span className="bse-auto"> (matched automatically{line.contractorMatchReasons ? `: ${line.contractorMatchReasons}` : ""})</span>}
          </div>
        )}
        {!matched && hasCandidates && <div className="bse-contractor__ask">Looks like a contractor payment. Pick one to confirm.</div>}
        {payroll && !matched && (
          <div className="bse-payroll">
            Payroll: counted once here. Not added to job labor or monthly expenses. Compare it with Tech Daily Pay in
            “Payroll vs Tech Daily Pay” above — that is a check only, never subtracted again.
          </div>
        )}
        {showPicker ? (
          renderContractorPicker(line, claimed, onPick)
        ) : (
          !compact && (
            <button type="button" className="fin-linkbtn fin-small" onClick={() => onPick("__show__")}>
              Match to contractor pay…
            </button>
          )
        )}
      </div>
    );
  };

  /** Link / unlink a saved line from the Recent entries list. */
  const setEntryContractorMatch = async (entry, pay) => {
    const oldId = entry.matchedContractorPayId;
    const oldCol = entry.matchedContractorPayCollection;
    const changes = pay
      ? contractorChanges(entry, pay, "manual")
      : { ...contractorChanges(entry, null), category: entry.category === "Subcontractor" ? "Subcontractor" : entry.category };
    await quickUpdateEntry(entry, changes);
    if (oldId && oldId !== pay?.id) await markContractorPayment(oldCol, oldId, null);
    if (pay) await markContractorPayment(pay.sourceCollection, pay.id, entry.id, entry);
  };

  const setImportRowPersonal = (row, on, type) => {
    const base = on && row.matchedContractorPayId ? { ...row, ...contractorChanges(row, null) } : row;
    const next = personalChanges(base, on, type);
    updateImportRow(row.tempId, next);
  };

  const addPersonalType = async () => {
    const name = String(window.prompt("New personal type (e.g. Personal Gym):") || "").trim();
    if (!name) return "";
    if (mergeCategories(customCats).some((c) => c.name.toLowerCase() === name.toLowerCase())) return name;
    const next = [...customCats, { name, kind: "owner_draw" }];
    try {
      await saveCustomCategories(next);
      setCustomCats(next);
      return name;
    } catch (err) {
      console.error(err);
      alert("Could not save the new personal type.");
      return "";
    }
  };

  const renderPersonalTypeSelect = (value, onPick) => (
    <select
      value={value || "Personal"}
      onChange={async (e) => {
        if (e.target.value === "__new__") {
          const added = await addPersonalType();
          if (added) onPick(added);
          return;
        }
        onPick(e.target.value);
      }}
      style={{ minWidth: 190, marginTop: 6 }}
    >
      {value && !personalTypes.includes(value) && <option value={value}>{value}</option>}
      {personalTypes.map((t) => (
        <option key={t} value={t}>{t === REPAYMENT_TYPE ? "Paying Back Reimbursable (not a draw)" : t}</option>
      ))}
      <option value="__new__">+ Add personal type…</option>
    </select>
  );

  // Running totals for the rows you're about to import.
  const reviewTotals = useMemo(() => {
    const t = { moneyIn: 0, reimbursableIn: 0, moneyOut: 0, business: 0, personal: 0, repaying: 0, other: 0, byType: {} };
    importRows
      .filter((r) => r.selected)
      .forEach((r) => {
        const amt = toNum(r.amount);
        if (r.type === "moneyIn") {
          t.moneyIn += amt;
          if (r.reimbursable) t.reimbursableIn += amt;
          return;
        }
        t.moneyOut += amt;
        if (entryIsPersonal(r) && personalTypeOf(r) === REPAYMENT_TYPE) {
          t.repaying += amt;
        } else if (entryIsPersonal(r)) {
          t.personal += amt;
          const k = personalTypeOf(r);
          t.byType[k] = (t.byType[k] || 0) + amt;
        } else if (countsAsBusinessOut(r)) {
          t.business += amt;
        } else {
          t.other += amt;
        }
      });
    return t;
  }, [importRows]);

  const visibleImportRows = importRows.filter((r) =>
    reviewFilter === "moneyOut"
      ? r.type !== "moneyIn"
      : reviewFilter === "moneyIn"
      ? r.type === "moneyIn"
      : reviewFilter === "personal"
      ? entryIsPersonal(r) || r.reimbursable
      : true
  );

  /* ================================================================== */
  /* Banks & Cards — which months have statements                       */
  /* ================================================================== */

  const statusMonths = useMemo(() => recentCompletedMonths(6).reverse(), []); // oldest → newest

  const uploadedKeys = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => {
      const m = toDateKey(e.date).slice(0, 7);
      if (m) set.add(`${entryAccountId(e, bankAccounts)}_${m}`);
    });
    return set;
  }, [entries, bankAccounts]);

  const typedKeys = useMemo(() => new Set(manualSummaries.map((m) => `${m.accountId}_${m.month}`)), [manualSummaries]);

  // Payroll on bank statements vs Tech Daily Pay, month by month (check only).
  const payrollCheck = useMemo(() => {
    const currentMonth = todayKey().slice(0, 7);
    const months = [...statusMonths, currentMonth];
    const { startKey, endKey } = monthsRange(months);
    const labor = summarizeDailyLabor(laborDays, usersToMap(laborUsers), startKey, endKey);
    const bankByMonth = bankPayrollByMonth(entries);
    const rows = months
      .map((m) => ({
        month: m,
        bank: bankByMonth[m] || 0,
        daily: labor.byMonth[m] || 0,
        current: m === currentMonth,
      }))
      .reverse();
    return { rows, hasAny: rows.some((r) => r.bank || r.daily) };
  }, [statusMonths, laborDays, laborUsers, entries]);

  // First month we expect statements for: the account's "Expect from" month if
  // you set one, otherwise the earlier of its first upload and the month it was added.
  const trackFromMonth = (account) => {
    if (account.trackFrom) return account.trackFrom;
    const candidates = entries
      .filter((e) => entryAccountId(e, bankAccounts) === account.id)
      .map((e) => toDateKey(e.date).slice(0, 7))
      .filter(Boolean);
    const created = toDateKey(account.createdAt).slice(0, 7);
    if (created) candidates.push(created);
    return candidates.sort()[0] || "";
  };

  const monthStatus = (account, month) => {
    if (uploadedKeys.has(`${account.id}_${month}`)) return "uploaded";
    if (typedKeys.has(`${account.id}_${month}`)) return "typed";
    const from = trackFromMonth(account);
    if (account.trackUploads === false || (from && month < from)) return "na";
    return "missing";
  };

  const missingUploads = useMemo(
    () =>
      bankAccounts
        .filter((a) => a.trackUploads !== false)
        .map((a) => ({ account: a, months: statusMonths.filter((m) => monthStatus(a, m) === "missing") }))
        .filter((x) => x.months.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bankAccounts, statusMonths, uploadedKeys, typedKeys, entries]
  );

  const updateAccountSetting = async (account, changes) => {
    try {
      await updateDoc(doc(db, "bankStatementAccounts", account.id), { ...changes, updatedAt: serverTimestamp() });
      setBankAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, ...changes } : a)));
    } catch (err) {
      console.error(err);
      alert("Could not update this account.");
    }
  };

  const shortMonth = (m) => {
    const [y, mo] = m.split("-").map(Number);
    return new Date(y, mo - 1, 1).toLocaleString("en-US", { month: "short", year: "2-digit" });
  };

  const bankTabStyle = (active) => ({
    padding: "9px 16px",
    border: "none",
    borderBottom: active ? "2px solid #1e40af" : "2px solid transparent",
    background: "none",
    color: active ? "#1e40af" : "#6b7280",
    fontWeight: 600,
    fontSize: 15,
    cursor: "pointer",
    width: "auto",
    margin: 0,
    borderRadius: 0,
  });

  return (
    <div style={{ padding: 20 }}>
      <style>{`@keyframes bank-entry-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {!loading && missingUploads.length > 0 && (
        <div className="bse-missing">
          <div className="bse-missing__icon" aria-hidden>!</div>
          <div>
            <strong>Missing statement uploads</strong>
            <ul>
              {missingUploads.map(({ account, months }) => (
                <li key={account.id}>
                  <b>{bankAccountLabel(account)}</b>: {months.map(shortMonth).join(", ")}
                </li>
              ))}
            </ul>
            <span className="bse-missing__hint">
              Upload those statements below, or type the month's totals on Monthly Summary. Profit Sharing will warn until they're in.
            </span>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e5e7eb", marginBottom: 18 }}>
        <button type="button" style={bankTabStyle(pageTab === "entries")} onClick={() => setPageTab("entries")}>
          Statement Entries
        </button>
        <button type="button" style={bankTabStyle(pageTab === "monthly")} onClick={() => setPageTab("monthly")}>
          Monthly Summary
        </button>
      </div>
      {pageTab === "monthly" ? (
        <MonthlyBankSummary />
      ) : (
      <>
      <h1>Bank Statement Entries</h1>
      <p>
        Upload each bank and card statement, then mark anything personal or reimbursable. Money-out can be linked to likely jobs by date.
      </p>

      <div className="bse-howto">
        <b>How money is counted:</b> customer payments (money in) come from <b>Deposits</b> (including the <b>Paid</b> button
        on Approved Invoices and the <b>Job Estimate Spreadsheet</b>), not from here, so a payment is never counted twice. Money in on a bank statement is kept as a record;
        tick <b>Reimbursable</b> when it's the owner's personal money or a loan that has to be paid back. Every money-out line
        counts as a business expense unless you tick <b>Personal</b> (owner draw) or it's a transfer. When you pay back
        reimbursable money, tick Personal and choose <b>Paying Back Reimbursable</b>.
      </div>
      <section className="fin-page fin-page--embedded bse-banks">
        <div className="fin-card">
          <div className="fin-card__head">
            <div>
              <h2 className="fin-card__title">Banks &amp; Cards</h2>
              <p className="fin-small fin-muted" style={{ margin: "2px 0 0" }}>
                Every bank account and card you use. Each month shows whether its statement is in.
              </p>
            </div>
            <button type="button" className="fin-btn fin-btn--primary fin-btn--sm" onClick={() => setShowAddBank((v) => !v)}>
              {showAddBank ? "Close" : "+ Add Bank or Card"}
            </button>
          </div>
          {showAddBank && (
            <div className="bse-addbank">
        <form onSubmit={saveBankAccount} style={{ display: "grid", gridTemplateColumns: "minmax(180px, 1fr) 130px minmax(160px, 1fr) 160px auto", gap: 8, alignItems: "end", margin: "12px 0 0", padding: 12, background: "#fff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
          <label>Bank Name
            <input value={bankAccountForm.bankName} onChange={(e) => setBankAccountForm((p) => ({ ...p, bankName: e.target.value }))} placeholder="PNC, Wells Fargo, Chase..." style={{ width: "100%" }} />
          </label>
          <label>Last 4
            <input value={bankAccountForm.accountLast4} onChange={(e) => setBankAccountForm((p) => ({ ...p, accountLast4: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="1234" maxLength={4} style={{ width: "100%" }} />
          </label>
          <label>Nickname / Statement Name
            <input value={bankAccountForm.nickname} onChange={(e) => setBankAccountForm((p) => ({ ...p, nickname: e.target.value }))} placeholder="Operating, Payroll, etc." style={{ width: "100%" }} />
          </label>
          <label>Account Type
            <select value={bankAccountForm.accountType || "operating"} onChange={(e) => setBankAccountForm((p) => ({ ...p, accountType: e.target.value }))} style={{ width: "100%" }}>
              <option value="operating">Operating / Bank</option>
              <option value="creditCard">Credit Card</option>
              <option value="personal">Personal</option>
            </select>
          </label>
          <button type="submit" className="blue-button" disabled={savingBankAccount}>
            {savingBankAccount ? <LoadingSpinner label="Saving..." /> : "Add Bank"}
          </button>
        </form>
            </div>
          )}
          <div className="fin-table-wrap">
            <table className="fin-table fin-table--compact">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Type</th>
                  {statusMonths.map((m) => <th key={m} className="bse-center">{shortMonth(m)}</th>)}
                  <th className="bse-center">Track</th>
                  <th>Expect from</th>
                </tr>
              </thead>
              <tbody>
                {bankAccounts.length === 0 && (
                  <tr><td colSpan={statusMonths.length + 4} className="fin-empty">No banks or cards yet. Click “+ Add Bank or Card”.</td></tr>
                )}
                {bankAccounts.map((account) => (
                  <tr key={account.id} className={account.trackUploads === false ? "is-muted" : ""}>
                    <td className="fin-strong">
                      {[account.bankName, account.accountLast4 ? `•••• ${account.accountLast4}` : ""].filter(Boolean).join(" ")}
                      {account.nickname && <div className="fin-small fin-muted">{account.nickname}</div>}
                    </td>
                    <td>{bankAccountTypeLabel(account.accountType)}</td>
                    {statusMonths.map((m) => {
                      const st = monthStatus(account, m);
                      return (
                        <td key={m} className="bse-center">
                          <span className={`bse-chip bse-chip--${st}`} title={
                            st === "uploaded" ? "Statement uploaded" : st === "typed" ? "Totals typed on Monthly Summary" : st === "missing" ? "No statement yet" : "Not expected"
                          }>
                            {st === "uploaded" ? "✓" : st === "typed" ? "Typed" : st === "missing" ? "Missing" : "—"}
                          </span>
                        </td>
                      );
                    })}
                    <td className="bse-center">
                      <input
                        type="checkbox"
                        checked={account.trackUploads !== false}
                        onChange={(e) => updateAccountSetting(account, { trackUploads: e.target.checked })}
                        title="Warn me when this account is missing a statement"
                        style={{ width: "auto", margin: 0 }}
                      />
                    </td>
                    <td>
                      <input
                        type="month"
                        className="fin-input fin-input--sm"
                        value={account.trackFrom || trackFromMonth(account)}
                        onChange={(e) => e.target.value && updateAccountSetting(account, { trackFrom: e.target.value })}
                        title="Only warn about months from here on"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="fin-page fin-page--embedded" style={{ marginBottom: 20 }}>
        <div className="fin-card">
          <div className="fin-card__head">
            <div>
              <h2 className="fin-card__title">Payroll vs Tech Daily Pay</h2>
              <p className="fin-small fin-muted" style={{ margin: "2px 0 0" }}>
                Payroll lines on the bank statements are what Profit counts. Tech Daily Pay is what was recorded on the
                Tech Daily Pay page for the same month. This is a check only — Tech Daily Pay is never subtracted again.
              </p>
            </div>
          </div>
          <div className="fin-table-wrap">
            <table className="fin-table fin-table--compact">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="fin-num">Payroll on bank statements</th>
                  <th className="fin-num">Tech Daily Pay recorded</th>
                  <th className="fin-num">Difference</th>
                </tr>
              </thead>
              <tbody>
                {!payrollCheck.hasAny && (
                  <tr><td colSpan={4} className="fin-empty">No payroll lines or Tech Daily Pay recorded in the last 6 months yet.</td></tr>
                )}
                {payrollCheck.hasAny && payrollCheck.rows.map((r) => {
                  const diff = Math.round((r.bank - r.daily) * 100) / 100;
                  return (
                    <tr key={r.month}>
                      <td className="fin-strong">{shortMonth(r.month)}{r.current ? " (so far)" : ""}</td>
                      <td className="fin-num">{money(r.bank)}</td>
                      <td className="fin-num">{money(r.daily)}</td>
                      <td className="fin-num" style={{ color: Math.abs(diff) >= 1 ? "#b45309" : "#166534", fontWeight: 700 }}>
                        {Math.abs(diff) < 1 ? "Matches" : `${diff > 0 ? "+" : "−"}${money(Math.abs(diff))}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={{ padding: 16, border: "2px solid #0f766e", borderRadius: 12, background: "#ecfdf5", marginBottom: 22 }}>
        <h2 style={{ marginTop: 0 }}>Upload Bank Statement PDF</h2>
        <p style={{ marginTop: 0 }}>
          First choose the bank and last 4 for the statement you are uploading. Then upload the text-based PDF. Every extracted transaction will be assigned to that selected account, and you can still change an individual row in the review table before importing.
        </p>
        <div style={{ padding: 12, background: "#fff", border: "1px solid #bbf7d0", borderRadius: 10, marginBottom: 12 }}>
          <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>Choose Bank / Last 4 for this PDF Upload</label>
          <select value={selectedUploadBankAccountId} onChange={(e) => applyBankAccountToImportRows(e.target.value)} style={{ minWidth: 320, maxWidth: "100%", marginBottom: 8 }}>
            <option value="">No saved bank selected</option>
            {bankAccounts.map((account) => (
              <option key={account.id} value={account.id}>{bankAccountLabel(account)}</option>
            ))}
          </select>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#14532d" }}>
            Pick the account before uploading. Credit card accounts will automatically treat money-in rows as credit card payments/transfers so they do not count as income.
          </p>
        </div>
        <input type="file" accept="application/pdf,.pdf" onChange={handlePdfUpload} disabled={importingPdf || importingRows} />
        {importingPdf && <p><LoadingSpinner label="Reading PDF statement..." /></p>}
        {pdfFileName && !importingPdf && <p><strong>Loaded:</strong> {pdfFileName}</p>}

        <div style={{ marginTop: 10 }}>
          <button type="button" className="blue-button" onClick={() => setShowRawText((v) => !v)}>
            {showRawText ? "Hide Raw Extracted Text" : "Show / Paste Raw Statement Text"}
          </button>
        </div>

        {showRawText && (
          <div style={{ marginTop: 10 }}>
            <textarea
              rows={8}
              value={rawExtractedText}
              onChange={(e) => setRawExtractedText(e.target.value)}
              placeholder="Extracted PDF text will appear here. You can also paste statement text here and click Re-Analyze Text."
              style={{ width: "100%" }}
            />
            <button type="button" className="blue-button" onClick={reparseRawText} style={{ marginTop: 8 }}>
              Re-Analyze Text
            </button>
          </div>
        )}

        {importRows.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h3>Review Extracted Rows</h3>
            <p>
              Review each row before importing. One Time is checked by default. Switch a row to Recurring only when it is a monthly/regular expense. Duplicate statement rows are skipped, and recurring rows are checked against saved monthly expenses so you do not create the same monthly twice.
            </p>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "10px 0 12px" }}>
              <button
                type="button"
                className="blue-button"
                onClick={() => setImportRows((prev) => prev.map((row) => ({ ...row, selected: true })))}
              >
                Select All Rows
              </button>
              <button
                type="button"
                className="gray-button"
                onClick={() => setImportRows((prev) => prev.map((row) => ({ ...row, selected: false })))}
              >
                Deselect All Rows
              </button>
              <span style={{ fontWeight: 700 }}>
                {importRows.filter((row) => row.selected).length} of {importRows.length} rows selected
              </span>
              <span className="bse-filter">
                Show:
                {[["all", "All rows"], ["moneyIn", "Money in"], ["moneyOut", "Money out"], ["personal", "Personal / Reimb."]].map(([k, label]) => (
                  <button key={k} type="button" className={reviewFilter === k ? "is-active" : ""} onClick={() => setReviewFilter(k)}>{label}</button>
                ))}
              </span>
            </div>

            <div className="bse-totals">
              <div><span>Money in</span><b>{money(reviewTotals.moneyIn)}</b></div>
              <div><span>Money out</span><b>{money(reviewTotals.moneyOut)}</b></div>
              <div className="is-business"><span>Business</span><b>{money(reviewTotals.business)}</b></div>
              <div className="is-personal"><span>Personal</span><b>{money(reviewTotals.personal)}</b></div>
              <div><span>Transfers / not counted</span><b>{money(reviewTotals.other)}</b></div>
            </div>
            {(reviewTotals.reimbursableIn > 0 || reviewTotals.repaying > 0) && (
              <div className="bse-reimb-break">
                {reviewTotals.reimbursableIn > 0 && <span><b>Reimbursable money in:</b> {money(reviewTotals.reimbursableIn)} (has to be paid back)</span>}
                {reviewTotals.repaying > 0 && <span><b>Paying back:</b> {money(reviewTotals.repaying)}</span>}
              </div>
            )}
            {reviewTotals.personal > 0 && (
              <div className="bse-personal-break">
                <b>Personal:</b>{" "}
                {Object.entries(reviewTotals.byType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => `${k} ${money(v)}`)
                  .join(" · ")}
              </div>
            )}
            <p className="fin-small fin-muted" style={{ margin: "6px 0 10px" }}>
              Tick <b>Personal</b> on any money-out line that wasn't for the business. It's saved as an owner draw: it shows up
              as personal, doesn't count as a business expense, and is only in the statement once, so it's never counted twice.
            </p>
            <div style={{ overflowX: "auto" }}>
              <table className="styled-table">
                <thead>
                  <tr>
                    <th>Import</th>
                    <th>Fee Type</th>
                    <th>Date</th>
                    <th style={{ minWidth: 120 }}>Type</th>
                    <th>Bank / Last 4</th>
                    <th>Amount</th>
                    <th style={{ minWidth: 220 }}>Personal / Reimbursable</th>
                    <th style={{ minWidth: 280 }}>Contractor / Payroll</th>
                    <th>Description</th>
                    <th>Ref #</th>
                    <th>Statement Details</th>
                    <th>Category</th>
                    <th>Money-In Match</th>
                    <th>Job Match</th>
                    <th>Accounting</th>
                    <th>Warnings</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleImportRows.map((row) => {
                    const rowMoneyMatches = findMoneyInMatchesForRow(row);
                    const rowJobMatches = findJobMatchesForRow(row);
                    const rowPersonal = row.type !== "moneyIn" && entryIsPersonal(row);

                    return (
                      <tr key={row.tempId} className={rowPersonal ? "bse-row-personal" : row.reimbursable ? "bse-row-reimb" : row.matchedContractorPayId ? "bse-row-contractor" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!row.selected}
                            onChange={(e) => updateImportRow(row.tempId, { selected: e.target.checked })}
                          />
                        </td>
                        <td>
                          <select
                            value={row.recurrenceType || "oneTime"}
                            onChange={(e) => updateImportRow(row.tempId, {
                              recurrenceType: e.target.value,
                              recurringFrequency: e.target.value === "recurring" ? row.recurringFrequency || "Monthly" : "Monthly",
                              monthlyDuplicateWarning: e.target.value === "recurring" && findMonthlyExpenseDuplicate({ ...row, recurrenceType: "recurring" }) ? "Possible recurring monthly duplicate" : "",
                            })}
                            style={{ minWidth: 130 }}
                          >
                            <option value="oneTime">One Time</option>
                            <option value="recurring">Recurring</option>
                          </select>
                          {row.recurrenceType === "recurring" && (
                            <select
                              value={row.recurringFrequency || "Monthly"}
                              onChange={(e) => updateImportRow(row.tempId, { recurringFrequency: e.target.value })}
                              style={{ minWidth: 130, marginTop: 6 }}
                            >
                              <option value="Monthly">Monthly</option>
                              <option value="Weekly">Weekly</option>
                              <option value="Bi-Weekly">Bi-Weekly</option>
                              <option value="Quarterly">Quarterly</option>
                              <option value="Annually">Annually</option>
                            </select>
                          )}
                        </td>
                        <td>
                          <input type="date" value={row.date || ""} onChange={(e) => updateImportRow(row.tempId, { date: e.target.value, duplicateKey: getBankEntryDuplicateKey({ ...row, date: e.target.value }) })} />
                        </td>
                        <td>
                          <select value={row.type} onChange={(e) => {
                            const next = { ...row, type: e.target.value, accountingAction: "" };
                            const guessed = { ...next, category: guessCategory(row.description, e.target.value, row.amount), categoryOther: "" };
                            updateImportRow(row.tempId, { ...guessed, accountingAction: getDefaultAccountingAction(guessed) });
                          }} style={{ minWidth: 120 }}>
                            <option value="moneyIn">Money In</option>
                            <option value="moneyOut">Money Out</option>
                          </select>
                        </td>
                        <td>
                          <select
                            value={row.bankAccountId || ""}
                            onChange={(e) => {
                              const account = bankAccounts.find((item) => item.id === e.target.value) || null;
                              const nextRow = applyBankAccountToEntry(row, account);
                              updateImportRow(row.tempId, {
                                bankAccountId: nextRow.bankAccountId,
                                bankName: nextRow.bankName,
                                accountLast4: nextRow.accountLast4,
                                accountType: nextRow.accountType,
                                accountingAction: nextRow.accountingAction,
                                duplicateKey: getBankEntryDuplicateKey(nextRow),
                              });
                            }}
                            style={{ minWidth: 190 }}
                          >
                            <option value="">No saved bank</option>
                            {bankAccounts.map((account) => (
                              <option key={account.id} value={account.id}>{bankAccountLabel(account)}</option>
                            ))}
                          </select>
                          {!row.bankAccountId && (
                            <div style={{ fontSize: 12, marginTop: 4 }}>{bankAccountLabel(row)}</div>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={row.amount}
                            onChange={(e) => updateImportRow(row.tempId, { amount: e.target.value })}
                            style={{ width: 110 }}
                          />
                        </td>
                        <td>
                          {row.type === "moneyIn" ? (
                            <>
                              <label className="bse-personal-check bse-reimb-check">
                                <input
                                  type="checkbox"
                                  checked={!!row.reimbursable}
                                  onChange={(e) => updateImportRow(row.tempId, reimbursableChanges(row, e.target.checked))}
                                />
                                Reimbursable
                              </label>
                              {row.reimbursable && renderReimbursableFields(row, (ch) => updateImportRow(row.tempId, ch))}
                            </>
                          ) : (
                            <>
                              <label className="bse-personal-check">
                                <input
                                  type="checkbox"
                                  checked={rowPersonal}
                                  onChange={(e) => setImportRowPersonal(row, e.target.checked)}
                                />
                                Personal
                              </label>
                              {rowPersonal && renderPersonalTypeSelect(row.personalType, (t) => updateImportRow(row.tempId, { personalType: t }))}
                            </>
                          )}
                        </td>
                        <td>
                          {renderContractorCell(row, {
                            claimed: claimedIds(row.tempId),
                            onPick: (pay) =>
                              pay === "__show__"
                                ? updateImportRow(row.tempId, { showContractorPicker: true })
                                : updateImportRow(row.tempId, pay ? contractorChanges(row, pay, "manual") : { ...contractorChanges(row, null) }),
                          })}
                        </td>
                        <td>
                          <input
                            value={row.description || ""}
                            onChange={(e) => updateImportRow(row.tempId, { description: e.target.value, vendorOrPayer: e.target.value.slice(0, 80) })}
                            style={{ minWidth: 260 }}
                          />
                        </td>
                        <td>
                          <input
                            value={row.referenceNumber || ""}
                            onChange={(e) => updateImportRow(row.tempId, { referenceNumber: e.target.value })}
                            placeholder="Reference #"
                            style={{ width: 130 }}
                          />
                        </td>
                        <td style={{ minWidth: 260, fontSize: 12 }}>
                          <div>{getEntryDetails(row) || "—"}</div>
                          {row.rawLine && (
                            <details style={{ marginTop: 4 }}>
                              <summary>Raw line</summary>
                              <div style={{ whiteSpace: "normal" }}>{row.rawLine}</div>
                            </details>
                          )}
                        </td>
                        <td>
                          <select
                            value={row.category || "Other"}
                            onChange={(e) => {
                              const next = getCategoryDrivenEntryChanges(row, e.target.value);
                              updateImportRow(row.tempId, { ...next, duplicateKey: getBankEntryDuplicateKey(next) });
                            }}
                          >
                            {renderCategoryOptions()}
                          </select>
                          {row.category === "Other" && (
                            <input
                              value={row.categoryOther || ""}
                              onChange={(e) => updateImportRow(row.tempId, { categoryOther: e.target.value })}
                              placeholder="Enter custom category"
                              style={{ marginTop: 6, minWidth: 190 }}
                            />
                          )}
                        </td>
                        <td>
                          {row.type === "moneyIn" ? (
                            <select
                              value={row.matchedSourceId ? `${row.matchedCollection}:${row.matchedSourceId}` : ""}
                              onChange={(e) => handleImportRowMatchChange(row.tempId, e.target.value)}
                              style={{ minWidth: 260 }}
                            >
                              <option value="">No match — count as new</option>
                              {rowMoneyMatches.map((match) => (
                                <option key={match.id} value={match.id}>{match.label}</option>
                              ))}
                            </select>
                          ) : "—"}
                        </td>
                        <td>
                          {row.type === "moneyOut" ? (
                            <select value={jobPickValue(row)} onChange={(e) => handleImportRowJobChange(row.tempId, e.target.value)} style={{ minWidth: 260 }}>
                              <option value="">Unmatched</option>
                              {row.jobId && !rowJobMatches.some((m) => jobOptionValue(m) === jobPickValue(row)) && (
                                <option value={jobPickValue(row)}>{row.wo || "Selected job"}{row.jobOccurrenceDate ? ` • visit ${row.jobOccurrenceDate}` : ""}</option>
                              )}
                              {rowJobMatches.map((match) => (
                                <option key={jobOptionValue(match)} value={jobOptionValue(match)}>{match.label}</option>
                              ))}
                            </select>
                          ) : "—"}
                        </td>
                        <td style={{ minWidth: 260 }}>
                          <select
                            value={getDefaultAccountingAction(row)}
                            onChange={(e) => updateImportRow(row.tempId, {
                              accountingAction: e.target.value,
                              matchedExistingPayment: e.target.value === "matched_existing_do_not_count" ? true : row.matchedExistingPayment,
                            })}
                            style={{ minWidth: 250 }}
                          >
                            {accountingActionOptionsForEntry(row).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                          <div style={{ fontSize: 12, marginTop: 4, color: accountingActionCounts(row) ? "#166534" : "#92400e", fontWeight: 700 }}>
                            {accountingActionCounts(row) ? "Will count" : "Record only / not counted"}
                          </div>
                        </td>
                        <td style={{ color: row.duplicateWarning || row.monthlyDuplicateWarning ? "#b45309" : "#166534", fontWeight: 700, minWidth: 180 }}>
                          {findExistingDuplicateEntry(row) ? "Already imported - will skip" : row.duplicateWarning || row.monthlyDuplicateWarning || (row.recurrenceType === "recurring" && findMonthlyExpenseDuplicate(row) ? "Possible monthly duplicate" : "OK")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button className="green-button" type="button" onClick={importSelectedRows} disabled={importingRows}>
              {importingRows ? <LoadingSpinner label="Importing..." /> : "Import Selected Rows"}
            </button>
          </div>
        )}
      </section>

      <section style={{ padding: 16, border: "1px solid #ddd", borderRadius: 12, background: "#f8fafc", marginBottom: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0 }}>Manual Bank Statement Entry</h2>
            <p style={{ margin: "6px 0 0", color: "#334155" }}>
              Use this only for one-off bank or credit-card transactions that you want to enter by hand instead of uploading from a PDF.
            </p>
          </div>
          <button type="button" className={showManualEntry ? "gray-button" : "blue-button"} onClick={() => setShowManualEntry((v) => !v)}>
            {showManualEntry ? "Hide Manual Bank Statement Entry" : "Show Manual Bank Statement Entry"}
          </button>
        </div>

        {showManualEntry && (
          <form onSubmit={saveEntry} style={{ display: "grid", gap: 10, maxWidth: 950, marginTop: 14, padding: 16, border: "1px solid #cbd5e1", borderRadius: 12, background: "#fff" }}>
            <p style={{ margin: 0, color: "#334155" }}>
              Choose the correct bank/last 4 first, then pick the category. Special categories like Credit Card Payment, Personal / Owner Draw, Personal Life Insurance, Owner Transfer, Internal Transfer, Refund, and Bank Correction automatically set the correct accounting treatment so they do not incorrectly count as income or expenses.
            </p>
        <label>Type</label>
        <select
          name="type"
          value={form.type}
          onChange={(e) => {
            setForm((p) => {
              const nextType = e.target.value;
              const next = {
                ...p,
                type: nextType,
                accountingAction: "",
                category: nextType === "moneyIn" ? "Customer Payment" : p.category,
                categoryOther: "",
              };
              return { ...next, accountingAction: getDefaultAccountingAction(next) };
            });
            setSelectedMoneyInMatch("");
            setSelectedJobMatch("");
          }}
        >
          <option value="moneyIn">Money In</option>
          <option value="moneyOut">Money Out</option>
        </select>

        <label>Date</label>
        <input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} required />

        <label>Amount</label>
        <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} required />

        <label>Matching Window</label>
        <select value={matchWindowDays} onChange={(e) => setMatchWindowDays(Number(e.target.value))}>
          <option value={3}>± 3 days</option>
          <option value={5}>± 5 days</option>
          <option value={10}>± 10 days</option>
          <option value={15}>± 15 days</option>
        </select>

        <label>Accounting Treatment</label>
        <select value={getDefaultAccountingAction(form)} onChange={(e) => setForm((p) => ({ ...p, accountingAction: e.target.value }))}>
          {accountingActionOptionsForEntry(form).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <div style={{ color: accountingActionCounts(form) ? "#166534" : "#92400e", fontWeight: 700 }}>
          {accountingActionCounts(form)
            ? "This entry will count in accounting totals."
            : "This entry will be saved for records only and will not count as income, expense, job profit, deposits, or monthly expense totals."}
        </div>

        <label>Fee Type</label>
        <select value={form.recurrenceType || "oneTime"} onChange={(e) => setForm((p) => ({ ...p, recurrenceType: e.target.value, recurringFrequency: e.target.value === "recurring" ? p.recurringFrequency || "Monthly" : "Monthly" }))}>
          <option value="oneTime">One Time</option>
          <option value="recurring">Recurring</option>
        </select>
        {form.recurrenceType === "recurring" && (
          <>
            <label>Recurring Frequency</label>
            <select value={form.recurringFrequency || "Monthly"} onChange={(e) => setForm((p) => ({ ...p, recurringFrequency: e.target.value }))}>
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Bi-Weekly">Bi-Weekly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Annually">Annually</option>
            </select>
            {findMonthlyExpenseDuplicate(form) && <div style={{ color: "#b45309", fontWeight: 700 }}>Possible duplicate monthly expense already exists. The bank entry can still be saved, but a duplicate monthly expense will not be created.</div>}
          </>
        )}

        {form.type === "moneyIn" && amount > 0 && entryDate && (
          <div style={{ padding: 12, border: "1px solid #bfdbfe", borderRadius: 10, background: "#eff6ff" }}>
            <strong>Possible Existing Payment Matches</strong>
            <p style={{ margin: "6px 0" }}>
              Select one only if this bank deposit is the same payment already entered elsewhere. If selected, this bank entry will be saved for recordkeeping but will not add the amount again.
            </p>
            <select value={selectedMoneyInMatch} onChange={(e) => handleMoneyInMatchChange(e.target.value)} style={{ width: "100%" }}>
              <option value="">No match — count this as new money in</option>
              {moneyInMatchCandidates.map((match) => (
                <option key={match.id} value={match.id}>{match.label}</option>
              ))}
            </select>
            {selectedMoneyInMatch && form.matchNote && (
              <div style={{ marginTop: 8, fontWeight: 700, color: "#92400e" }}>{form.matchNote}</div>
            )}
            {moneyInMatchCandidates.length === 0 && <div style={{ marginTop: 6 }}>No existing payment found within the selected date window and $150 amount-review tolerance, including fee-adjusted matches.</div>}
          </div>
        )}

        <label>Bank / Last 4</label>
        <select value={form.bankAccountId || ""} onChange={(e) => handleManualBankAccountChange(e.target.value)}>
          <option value="">Select saved bank / account</option>
          {bankAccounts.map((account) => (
            <option key={account.id} value={account.id}>{bankAccountLabel(account)}</option>
          ))}
        </select>

        <label>Bank Name</label>
        <input value={form.bankName} onChange={(e) => setForm((p) => ({ ...p, bankAccountId: "", bankName: e.target.value }))} placeholder="Bank name" />

        <label>Account Last 4</label>
        <input value={form.accountLast4 || ""} onChange={(e) => setForm((p) => ({ ...p, bankAccountId: "", accountLast4: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="Last 4 digits" maxLength={4} />

        <label>Statement / Transaction Reference #</label>
        <input value={form.referenceNumber || ""} onChange={(e) => setForm((p) => ({ ...p, referenceNumber: e.target.value }))} placeholder="Reference number from bank statement" />

        <label>Statement Section / Detail</label>
        <input value={form.statementSection || ""} onChange={(e) => setForm((p) => ({ ...p, statementSection: e.target.value }))} placeholder="ACH Deductions, Deposits, Other Additions, etc." />

        <label>{form.type === "moneyIn" ? "Payer / From" : "Vendor / Paid To"}</label>
        <input value={form.vendorOrPayer} onChange={(e) => setForm((p) => ({ ...p, vendorOrPayer: e.target.value }))} />

        <label>Category</label>
        <select
          value={form.category}
          onChange={(e) => setForm((p) => {
            return getCategoryDrivenEntryChanges(p, e.target.value);
          })}
        >
          {renderCategoryOptions()}
        </select>
        {form.category === "Other" && (
          <>
            <label>Other Category</label>
            <input
              value={form.categoryOther || ""}
              onChange={(e) => setForm((p) => ({ ...p, categoryOther: e.target.value }))}
              placeholder="Type the category name"
            />
          </>
        )}

        {form.type === "moneyOut" && amount > 0 && entryDate && (
          <div style={{ padding: 12, border: "1px solid #fed7aa", borderRadius: 10, background: "#fff7ed" }}>
            <strong>Possible Job Matches by Date</strong>
            <p style={{ margin: "6px 0" }}>
              Select a likely job if this purchase belongs to a job near the statement date. If you leave it unmatched, it still counts as money spent.
            </p>
            <select value={selectedJobMatch} onChange={(e) => fillFromJob(e.target.value)} style={{ width: "100%" }}>
              <option value="">Leave unmatched</option>
              {jobMatchCandidates.map((match) => (
                <option key={jobOptionValue(match)} value={jobOptionValue(match)}>{match.label}</option>
              ))}
            </select>
            {jobMatchCandidates.length === 0 && <div style={{ marginTop: 6 }}>No close job dates found. This expense can still be saved unmatched.</div>}
          </div>
        )}

        <label>Link to Job (optional)</label>
        <select value={form.jobId} onChange={(e) => fillFromJob(e.target.value)}>
          <option value="">Unmatched / No job selected</option>
          {sortedJobs.map((job) => (
            <option key={job.id} value={job.id}>
              {job.wo || "No WO"} - {bankJobSite(job)} - {bankJobScope(job)}{isRecurring(job) ? " (Recurring)" : ""}
            </option>
          ))}
        </select>
        {form.jobId && form.jobOccurrenceDate && (
          <div style={{ fontSize: 13, color: "#334155" }}>Recurring job visit: <b>{form.jobOccurrenceDate}</b></div>
        )}

        <label>WO / Job Number</label>
        <input value={form.wo} onChange={(e) => setForm((p) => ({ ...p, wo: e.target.value }))} />

        <label>Invoice Number (for Money In matching)</label>
        <input value={form.invoiceNumber} onChange={(e) => setForm((p) => ({ ...p, invoiceNumber: e.target.value }))} list="invoiceNumbers" />
        <datalist id="invoiceNumbers">
          {approvedInvoices.map((invoice) => (
            <option key={invoice.id} value={getApprovedInvoiceNumber(invoice)}>
              {invoice.siteName || invoice.propertyName || "Invoice"} - {money(invoice.amount || invoice.rate)}
            </option>
          ))}
        </datalist>

        <label>Property / Site</label>
        <input value={form.property} onChange={(e) => setForm((p) => ({ ...p, property: e.target.value }))} />

        <label>Description / Memo</label>
        <textarea rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />

            <button className="green-button" type="submit" disabled={saving}>{saving ? <LoadingSpinner label="Saving..." /> : "Save Bank Statement Entry"}</button>
          </form>
        )}
      </section>

      {editingEntryId && editEntryForm && (
        <BankPopup open onClose={() => !saving && cancelEditEntry()} width={920} label="Edit bank statement entry">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 16px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20 }}>Edit Bank Statement Entry</h2>
                <div style={{ fontSize: 13, color: "#4b5563", marginTop: 3 }}>{editEntryForm.date || "No date"} • {money(editEntryForm.amount)} • {bankAccountLabel(editEntryForm)}</div>
              </div>
              <button type="button" className="gray-button" onClick={cancelEditEntry} aria-label="Close edit popup" disabled={saving} style={{ fontSize: 20, lineHeight: 1, padding: "6px 11px" }}>×</button>
            </div>

            <div style={{ padding: 16, overflowY: "auto", flex: 1, minHeight: 0 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
                <label>Date
                  <input type="date" value={editEntryForm.date || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, date: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Type
                  <select value={editEntryForm.type || "moneyOut"} onChange={(e) => setEditEntryForm((p) => {
                    const next = { ...p, type: e.target.value, accountingAction: "" };
                    return { ...next, accountingAction: getDefaultAccountingAction(next) };
                  })} style={{ width: "100%" }}>
                    <option value="moneyIn">Money In</option>
                    <option value="moneyOut">Money Out</option>
                  </select>
                </label>

                <label>Amount
                  <input type="number" step="0.01" value={editEntryForm.amount || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, amount: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Saved Bank / Account
                  <select value={editEntryForm.bankAccountId || ""} onChange={(e) => handleEditBankAccountChange(e.target.value)} style={{ width: "100%" }}>
                    <option value="">Manual bank/account</option>
                    {bankAccounts.map((account) => <option key={account.id} value={account.id}>{bankAccountLabel(account)}</option>)}
                  </select>
                </label>

                <label>Bank Name
                  <input value={editEntryForm.bankName || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, bankAccountId: "", bankName: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Last 4
                  <input value={editEntryForm.accountLast4 || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, bankAccountId: "", accountLast4: e.target.value.replace(/\D/g, "").slice(0, 4) }))} maxLength={4} style={{ width: "100%" }} />
                </label>

                <label>Account Type
                  <select value={editEntryForm.accountType || "operating"} onChange={(e) => setEditEntryForm((p) => ({ ...p, accountType: e.target.value, accountingAction: getDefaultAccountingAction({ ...p, accountType: e.target.value }) }))} style={{ width: "100%" }}>
                    <option value="operating">Operating / Bank</option>
                    <option value="creditCard">Credit Card</option>
                    <option value="personal">Personal</option>
                  </select>
                </label>

                <label>Fee Type
                  <select value={editEntryForm.recurrenceType || "oneTime"} onChange={(e) => setEditEntryForm((p) => ({ ...p, recurrenceType: e.target.value }))} style={{ width: "100%" }}>
                    <option value="oneTime">One Time</option>
                    <option value="recurring">Recurring</option>
                  </select>
                </label>

                {editEntryForm.recurrenceType === "recurring" && (
                  <label>Frequency
                    <select value={editEntryForm.recurringFrequency || "Monthly"} onChange={(e) => setEditEntryForm((p) => ({ ...p, recurringFrequency: e.target.value }))} style={{ width: "100%" }}>
                      <option value="Monthly">Monthly</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Bi-Weekly">Bi-Weekly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Annually">Annually</option>
                    </select>
                  </label>
                )}

                <label>Accounting Action
                  <select value={editEntryForm.accountingAction || getDefaultAccountingAction(editEntryForm)} onChange={(e) => setEditEntryForm((p) => ({ ...p, accountingAction: e.target.value }))} style={{ width: "100%" }}>
                    {accountingActionOptionsForEntry(editEntryForm).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>

                <label>Vendor / Payer
                  <input value={editEntryForm.vendorOrPayer || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, vendorOrPayer: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Category
                  <select value={editEntryForm.category || "Other"} onChange={(e) => setEditEntryForm((p) => getCategoryDrivenEntryChanges(p, e.target.value))} style={{ width: "100%" }}>
                    {renderCategoryOptions()}
                  </select>
                </label>

                {editEntryForm.type === "moneyIn" && (
                  <label>Reimbursable?
                    <div>
                      <label className="bse-personal-check bse-reimb-check">
                        <input
                          type="checkbox"
                          checked={!!editEntryForm.reimbursable}
                          onChange={(e) => setEditEntryForm((p) => reimbursableChanges(p, e.target.checked))}
                        />
                        Has to be paid back
                      </label>
                      {editEntryForm.reimbursable &&
                        renderReimbursableFields(editEntryForm, (ch) => setEditEntryForm((p) => ({ ...p, ...ch })))}
                    </div>
                  </label>
                )}
                {editEntryForm.type !== "moneyIn" && (
                  <label>Personal?
                    <div>
                      <label className="bse-personal-check">
                        <input
                          type="checkbox"
                          checked={entryIsPersonal(editEntryForm)}
                          onChange={(e) => setEditEntryForm((p) => personalChanges(p, e.target.checked))}
                        />
                        Personal (owner draw)
                      </label>
                      {entryIsPersonal(editEntryForm) &&
                        renderPersonalTypeSelect(editEntryForm.personalType, (t) => setEditEntryForm((p) => ({ ...p, personalType: t })))}
                    </div>
                  </label>
                )}
                {editEntryForm.category === "Other" && (
                  <label>Other Category
                    <input value={editEntryForm.categoryOther || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, categoryOther: e.target.value }))} style={{ width: "100%" }} />
                  </label>
                )}

                <label>WO / Job Number
                  <input value={editEntryForm.wo || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, wo: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Invoice Number
                  <input value={editEntryForm.invoiceNumber || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, invoiceNumber: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Reference #
                  <input value={editEntryForm.referenceNumber || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, referenceNumber: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Statement Section
                  <input value={editEntryForm.statementSection || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, statementSection: e.target.value }))} style={{ width: "100%" }} />
                </label>

                <label>Property / Site
                  <input value={editEntryForm.property || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, property: e.target.value }))} style={{ width: "100%" }} />
                </label>
              </div>

              <label style={{ display: "block", marginTop: 12 }}>Description / Memo
                <textarea rows={4} value={editEntryForm.description || ""} onChange={(e) => setEditEntryForm((p) => ({ ...p, description: e.target.value }))} style={{ width: "100%" }} />
              </label>
            </div>

            <div style={{ position: "sticky", bottom: 0, padding: 12, borderTop: "1px solid #e5e7eb", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="gray-button" onClick={cancelEditEntry} disabled={saving}>Cancel</button>
              <button type="button" className="green-button" onClick={saveEditedEntry} disabled={saving}>{saving ? <LoadingSpinner label="Saving..." /> : "Save Changes"}</button>
            </div>
        </BankPopup>
      )}

      <section style={{ marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0 }}>Recent Bank Statement Entries</h2>
            <p style={{ margin: "6px 0 0" }}>
              Recent entries are hidden by default. Open a month/year, then open the bank statement group you want to review.
            </p>
          </div>
          <button type="button" className="blue-button" onClick={() => setShowRecentEntries((prev) => !prev)}>
            {showRecentEntries ? "Hide Recent Entries" : `Show Recent Entries (${entries.length})`}
          </button>
        </div>

        {showRecentEntries && (
          <div style={{ marginTop: 14 }}>
            {loading ? <p><LoadingSpinner label="Loading..." /></p> : groupedRecentEntries.length === 0 ? (
              <p>No bank statement entries saved yet.</p>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {groupedRecentEntries.map((monthGroup) => {
                  const monthOpen = !!openEntryMonths[monthGroup.key];
                  return (
                    <div key={monthGroup.key} style={{ border: "1px solid #d1d5db", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
                      <button
                        type="button"
                        onClick={() => toggleEntryMonth(monthGroup.key)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          border: 0,
                          background: "#f3f4f6",
                          padding: "12px 14px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          fontWeight: 800,
                          color: "#111827",
                        }}
                      >
                        <span>{monthOpen ? "▼" : "▶"} {monthGroup.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>
                          {monthGroup.entries.length} entries • In {money(monthGroup.moneyInTotal)} • Out {money(monthGroup.moneyOutTotal)} • Personal {money(monthGroup.personalTotal)} • Record Only {money(monthGroup.recordOnlyTotal)}
                          {(() => {
                            const payroll = monthGroup.entries.filter(isBankPayrollLine).reduce((s, e) => s + toNum(e.amount), 0);
                            return payroll > 0 ? ` • Payroll ${money(payroll)}` : "";
                          })()}
                        </span>
                      </button>

                      {monthOpen && (
                        <div style={{ padding: 12, display: "grid", gap: 10 }}>
                          {monthGroup.statements.map((statementGroup) => {
                            const statementOpen = !!openEntryStatements[statementGroup.key];
                            return (
                              <div key={statementGroup.key} style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                                <button
                                  type="button"
                                  onClick={() => toggleEntryStatement(statementGroup.key)}
                                  style={{
                                    width: "100%",
                                    textAlign: "left",
                                    border: 0,
                                    background: "#ecfdf5",
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    alignItems: "center",
                                    color: "#064e3b",
                                    fontWeight: 800,
                                  }}
                                >
                                  <span>{statementOpen ? "▼" : "▶"} {statementGroup.label}</span>
                                  <span style={{ fontSize: 12 }}>
                                    {statementGroup.entries.length} rows • In {money(statementGroup.moneyInTotal)} • Out {money(statementGroup.moneyOutTotal)} • Personal {money(statementGroup.personalTotal)} • Record Only {money(statementGroup.recordOnlyTotal)}
                                  </span>
                                </button>

                                {statementOpen && (
                                  <div style={{ overflowX: "auto" }}>
                                    <table className="styled-table" style={{ margin: 0 }}>
                                      <thead>
                                        <tr>
                                          <th>Date</th><th>Bank / Last 4</th><th style={{ minWidth: 120 }}>Type</th><th>Fee Type</th><th>Amount</th><th style={{ minWidth: 200 }}>Personal / Reimb.</th><th>Vendor/Payer</th><th>Category</th><th>WO</th><th>Invoice #</th><th>Ref #</th><th>Accounting</th><th>Details</th><th>Description</th><th>Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {statementGroup.entries.map((entry) => (
                                          <tr key={entry.id} className={entryIsPersonal(entry) ? "bse-row-personal" : entry.reimbursable ? "bse-row-reimb" : entry.matchedContractorPayId ? "bse-row-contractor" : ""}>
                                            <td>{entry.date}</td>
                                            <td>{bankAccountLabel(entry)}</td>
                                            <td>{entry.type === "moneyIn" ? "Money In" : "Money Out"}</td>
                                            <td>{entry.recurrenceType === "recurring" ? `Recurring${entry.recurringFrequency ? ` - ${entry.recurringFrequency}` : ""}` : "One Time"}</td>
                                            <td>{money(entry.amount)}</td>
                                            <td>
                                              {entry.type === "moneyIn" ? (
                                                <>
                                                  <label className="bse-personal-check bse-reimb-check">
                                                    <input
                                                      type="checkbox"
                                                      checked={!!entry.reimbursable}
                                                      disabled={saving}
                                                      onChange={(e) => {
                                                        if (!e.target.checked) {
                                                          quickUpdateEntry(entry, reimbursableChanges(entry, false));
                                                          return;
                                                        }
                                                        const note = window.prompt("Note (who it's from and how it gets paid back):", entry.reimbursableNote || "");
                                                        if (note === null) return;
                                                        quickUpdateEntry(entry, reimbursableChanges(entry, true, { reimbursableNote: note }));
                                                      }}
                                                    />
                                                    Reimbursable
                                                  </label>
                                                  {entry.reimbursable && (
                                                    <div className="fin-small" style={{ color: "#c2410c", marginTop: 4 }}>
                                                      {entry.reimbursableFrom === "other" ? "Loan / other" : "From owner"}
                                                      {entry.reimbursableNote ? ` · ${entry.reimbursableNote}` : ""}
                                                    </div>
                                                  )}
                                                </>
                                              ) : (
                                                <>
                                                  <label className="bse-personal-check">
                                                    <input
                                                      type="checkbox"
                                                      checked={entryIsPersonal(entry)}
                                                      disabled={saving}
                                                      onChange={(e) => quickUpdateEntry(entry, personalChanges(entry, e.target.checked))}
                                                    />
                                                    Personal
                                                  </label>
                                                  {entryIsPersonal(entry) &&
                                                    renderPersonalTypeSelect(entry.personalType, (t) => quickUpdateEntry(entry, { personalType: t }))}
                                                  {!entryIsPersonal(entry) && (
                                                    <div style={{ marginTop: 6 }}>
                                                      {entry.matchedContractorPayId || looksLikeContractorPay(entry) || openContractorPickers[entry.id]
                                                        ? renderContractorCell(entry, {
                                                            claimed: new Set(entries.filter((e) => e.id !== entry.id && e.matchedContractorPayId).map((e) => e.matchedContractorPayId)),
                                                            compact: true,
                                                            onPick: (pay) => pay !== "__show__" && setEntryContractorMatch(entry, pay),
                                                          })
                                                        : (
                                                          <button type="button" className="fin-linkbtn fin-small" onClick={() => setOpenContractorPickers((p) => ({ ...p, [entry.id]: true }))}>
                                                            Match to contractor pay…
                                                          </button>
                                                        )}
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </td>
                                            <td>{entry.vendorOrPayer}</td>
                                            <td>
                                              {entryIsPersonal(entry) ? (
                                                <span className="bse-personal-tag">Personal · {personalTypeOf(entry)}</span>
                                              ) : entry.reimbursable ? (
                                                <span className="bse-reimb-tag">Reimbursable</span>
                                              ) : entry.matchedContractorPayId ? (
                                                <span className="bse-contractor-tag">Contractor · {entry.matchedContractorName}</span>
                                              ) : effectiveCategory(entry)}
                                            </td>
                                            <td>{entry.wo}</td>
                                            <td>{entry.invoiceNumber || entry.matchedInvoiceNumber}</td>
                                            <td>{entry.referenceNumber || ""}</td>
                                            <td>{accountingActionLabel(entry)}</td>
                                            <td>{getEntryDetails(entry)}</td>
                                            <td>{entry.description || entry.auditNote}</td>
                                            <td>
                                              <button
                                                type="button"
                                                className="blue-button"
                                                onClick={() => startEditEntry(entry)}
                                                disabled={saving}
                                                style={{ marginRight: 6 }}
                                              >
                                                Edit
                                              </button>
                                              <button
                                                type="button"
                                                className="red-button"
                                                onClick={() => deleteBankStatementEntry(entry)}
                                                disabled={saving}
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>
      </>
      )}
    </div>
  );
}
